/**
 * 서버 구성 편집기 (별탭) — 서버 구성 V2.
 *
 * 진입 (딜 컨텍스트 필수):
 *   /deal/:dealId/config/:configId/server/new          — 신규
 *   /deal/:dealId/config/:configId/server/:serverItemId — 수정
 *
 * 저장 원천은 딜의 `deal_item` 서브트리다(server_config 테이블 없음).
 *   - 신규: 빈 상태 → Chassis 선택(취소 불가) → 완료 시 POST /deal/:id/server
 *   - 수정: 그 딜의 서버 사본을 로드 → 완료 시 PUT /deal/server/:id (자식행 diff)
 *
 * 완료가 유일한 커밋 지점이다. 중간 이탈 시 작업 내용은 사라지지만
 * 쓰레기 데이터도 남지 않는다(빈 구성 탭 하나가 최대 잔여물).
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
// 딥 경로 import — 배럴은 브라우저 번들에서 hono/jwt 해석 실패.
import {
  toSpecCategory, specCategoryOrder, SPEC_CATEGORIES, dbCategoriesFor,
  type SpecCategory,
} from '@hcltech/shared/server-config/spec-category';
import {
  buildLineShorthand,
} from '@hcltech/shared/server-config/shorthand';
import {
  type ServerConfigData, type ConfigRow,
  emptyRowSearch, emptyRowRegister, emptyChassisModal, emptyCandidates, emptySources, emptyCandidateModal,
  emptyBaseModal, newBaseRow,
  blankRow, rowFromDealItem, newRowKey, filterPool,
  CAND_LIMIT_INITIAL, CAND_LIMIT_STEP,
} from './server-config/types';
import {
  renderHeader, renderGrid, renderChassisModal, renderCandidates,
  renderCandidateModal, renderCandidatePool, renderBaseModal, renderBaseRows,
  renderHeaderNotice, renderTotals, renderRowSearchPanel, renderBaseBanners,
  renderRowSearchBody,
  renderChassisHits, renderChassisStaged, renderChassisRegisterRegion, renderChassisFoot,
  renderSourceSelect, serverConfigTitle,
  chassisRow, parsePrice, parseFx, descCellValue, mirroredBundle, rowMarginText, rowRateText,
  costKrw, nativeCostTitle,
} from './server-config/render';
import { sourceSnapshot } from '../lib/source-options';
import { publishDealChanged } from '../lib/deal-channel';

export interface ServerConfigLoaded {
  full: api.DealFull;
}

export class ServerConfigView extends SolaView<ServerConfigData> {
  override events = {
    click: {
      '@done':             'handleDone',
      '@chassis-replace':  'handleChassisReplaceOpen',
      '@chassis-pick':     'handleChassisPick',
      '@chassis-confirm':  'handleChassisConfirm',
      '@chassis-cancel':   'handleChassisCancel',
      '@chassis-register-open':  'handleChassisRegisterOpen',
      '@chassis-register-close': 'handleChassisRegisterClose',
      '@chassis-reg-confirm':    'handleChassisRegisterConfirm',
      '@row-delete':       'handleRowDelete',
      '@row-duplicate':    'handleRowDuplicate',
      '@row-search-open':  'handleRowSearchOpen',
      '@hit-pick':         'handleHitPick',
      // ★ Sola 위임은 **선언 순서 first-match-wins** 다(SolaView.ts:195~210).
      //   closest() 로 조상까지 올라가므로, 컨테이너보다 안쪽 요소를 먼저
      //   선언해야 한다. 아래 둘은 컨테이너 안에 들어 있다:
      //     cand-manage → <summary>(@cand-toggle) 안
      //   순서를 뒤집으면 '후보 관리' 를 눌러도 아코디언만 토글된다.
      //   (cand-add 는 본문에 있는데, 본문 자체엔 액션이 없어 충돌하지 않는다 —
      //    접기/펼치기는 summary 만 담당한다)
      '@cand-manage':      'handleCandManageOpen',
      '@cand-add':         'handleCandidateAdd',
      '@cand-toggle':      'handleCandToggle',
      '@cand-check':       'handleCandCheck',
      '@cand-check-all':   'handleCandCheckAll',
      '@cand-pool-more':   'handleCandPoolMore',
      '@cand-save':        'handleCandSave',
      '@cand-cancel':      'handleCandCancel',
      '@base-open':        'handleBaseOpen',
      '@base-add':         'handleBaseAdd',
      '@base-del':         'handleBaseDelete',
      '@base-search-open': 'handleBaseSearchOpen',
      '@base-done':        'handleBaseDone',
      '@base-cancel':      'handleBaseCancel',
      '@base-swap-apply':  'handleBaseSwapApply',
      '@base-swap-close':  'handleBaseSwapDismiss',
      '@base-retry':       'handleBaseRetry',
      '@row-register':     'handleRowRegister',       // 폼 열기
      '@row-reg-save':     'handleRowRegisterSave',
      '@row-reg-cancel':   'handleRowRegisterCancel',
      // ★ backdrop 은 **반드시 맨 마지막**. Sola 위임은 closest() + 선언 순서
      //   first-match-wins 라, backdrop 을 위에 두면 모달 안 버튼을 눌러도
      //   조상인 backdrop 이 먼저 매칭돼 안쪽 액션이 전부 죽는다.
      //   (핸들러의 e.target 확인은 "안쪽 클릭이 닫히는 것"만 막을 뿐,
      //    가로채기 자체는 순서로만 막힌다)
      '@cand-backdrop':    'handleCandBackdrop',
      '@base-backdrop':    'handleBaseBackdrop',
      '@chassis-backdrop': 'handleChassisBackdrop',
    },
    input: {
      '@cand-pool-search': 'handleCandPoolSearch',
      '@bundle-input':     'handleBundleInput',
      '@desc-input':       'handleDescInput',
      '@qty-input':        'handleQtyInput',
      '@base-desc':        'handleBaseDesc',
      '@base-qty':         'handleBaseQty',
      '@price-input':      'handlePriceInput',
      '@cost-input':       'handleCostInput',
      '@blank-search':     'handleBlankSearch',
      '@panel-search':     'handlePanelSearch',
      '@chassis-search':   'handleChassisSearch',
      '@chassis-reg-part': 'handleChassisRegPart',
      '@chassis-reg-desc': 'handleChassisRegDesc',
      '@row-reg-part':     'handleRowRegPart',
      '@row-reg-desc':     'handleRowRegDesc',
    },
    change: {
      '@category-select':  'handleCategorySelect',
      '@chassis-cond':     'handleChassisCond',
      '@source-select':    'handleSourceSelect',
      '@cand-source':      'handleCandSourceSelect',
      '@base-cat':         'handleBaseCategory',
      '@row-reg-cat':      'handleRowRegCategory',
    },
    // single click = stage, double click = stage 후 완료 경로 실행(같은 조건 검증).
    dblclick: {
      '@chassis-pick':     'handleChassisPickConfirm',
    },
  };

  // ─── 검색 디바운스 / stale 응답 차단 ───────────────────────────────
  //   키 입력마다 요청을 보내면 서버를 두드리고, 늦게 온 이전 응답이 최신 결과를
  //   덮어써 목록이 튄다. 타이머로 묶고 seq 로 순서를 보장한다.
  private static readonly SEARCH_DEBOUNCE_MS = 250;

  /**
   * 검색 UNION 이 미등록(catalog_part_pending) 행에 붙이는 source 라벨.
   * 카탈로그 테이블 이름이 아니다 — 정본은 백엔드 search.queries.ts 의 4-bis 블록.
   */
  private static readonly PENDING_SOURCE = 'pending';
  private chassisSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private chassisSearchSeq = 0;
  private rowSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private rowSearchSeq = 0;
  private candSeq = 0;
  private sourcesSeq = 0;
  /** 후보 관리 모달의 pool 응답 순서 보장 + 취소 시 무효화. */
  private poolSeq = 0;
  /** 기본 구성 조회 응답 순서 보장 + 취소 시 무효화. */
  private baseSeq = 0;

  constructor(params: {
    dealId: string; configId: string; serverItemId?: string;
  } & ServerConfigLoaded) {
    super();
    const full = params.full;

    // edit 모드 — 그 서버 헤더행 + 자식행을 딜 사본에서 로드한다(원본 X).
    const items = full.items ?? [];
    const server = params.serverItemId
      ? items.find((it) => String(it.id) === params.serverItemId && it.line_type === 'server')
      : undefined;
    const children = server
      ? items.filter((it) => it.parent_id != null && String(it.parent_id) === String(server.id))
      : [];

    const rows: ConfigRow[] = children.map(rowFromDealItem);

    this.data = {
      mode:            server ? 'edit' : 'new',
      dealId:          params.dealId,
      dealName:        full.name ?? '',
      // 구성명은 딜명이 아니라 거래처로 시작한다(2026-08-14 서안드레).
      customerName:    full.customer_name ?? '',
      configId:        params.configId,
      serverItemId:    server ? String(server.id) : null,
      expectedVersion: full.version,

      serverCondition: server?.server_condition ?? '',
      // 후보 응답이 도착해야 채워진다(applyCandidates). 그 전엔 약식 첫 줄 폴백.
      chassisModel:    null,
      bundlePrice:     server?.unit_price_override == null
        ? '' : server.unit_price_override.toLocaleString('ko-KR'),
      // 저장된 번들가와 구성품 합계의 관계로 되살린다 — types.ts 주석 참조.
      //   rows 는 위에서 이미 만들었으므로 여기서 바로 판정할 수 있다.
      bundleManual:    ServerConfigView.bundleWasManual(server?.unit_price_override ?? null, rows),
      // 오늘 환율은 onMount 에서 받아 채운다. 저장하지 않는다(types.ts 주석).
      fxRate:          '',

      rows,
      rowSearch:    emptyRowSearch(),
      // 신규 모드는 Chassis 선택을 강제한다(취소 불가).
      chassisModal: server
        ? emptyChassisModal()
        : { ...emptyChassisModal(), open: true, replace: false },
      candidates:     emptyCandidates(),
      candidateModal: emptyCandidateModal(),
      baseModal:      emptyBaseModal(),
      baseSwap:       null,
      baseLoadError:  null,
      sources:        emptySources(),
      candidateSource: {},

      saving: false,
      error:  '',
    };
  }

  /** 수정 모드로 열렸고 이미 Chassis 가 있으면 마운트 직후 후보를 불러온다. */
  override onMount(): void {
    const c = chassisRow(this.data);
    if (c?.optionPart) void this.loadCandidates(c.optionPart);
    void this.loadSources();
    void this.fillMissingSpecs();
    void this.loadFxRate();

    // Esc — 열린 레이어를 위에서부터 하나만 닫는다.
    this._keydown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (this.closeTopLayer()) e.preventDefault();
    };
    window.addEventListener('keydown', this._keydown);

    // 검색 팝오버는 position:fixed 라 좌표가 viewport 기준이다. 열 때 한 번 찍은
    // 좌표를 그대로 두면 페이지가 스크롤될 때 앵커만 움직여 팝오버가 떨어진다.
    // capture:true — window 뿐 아니라 내부 스크롤 컨테이너(기본 구성 모달 등)의
    // scroll 도 잡는다. scroll 은 버블링하지 않아 캡처가 아니면 놓친다.
    this._reanchor = () => this.repositionSearchPanel();
    window.addEventListener('scroll', this._reanchor, true);
    window.addEventListener('resize', this._reanchor);

    // 팝오버 바깥을 클릭하면 닫는다(서안드레 — Esc 만으로는 부족).
    //
    // pointerdown 이 아니라 **click** 을 쓴다. pointerdown 은 Sola 의 위임
    // 클릭 핸들러보다 먼저 터져서, 결과 항목을 고르는 순간 팝오버를 먼저
    // 닫아 버리면 @hit-pick 이 아예 실행되지 않는다. click 은 뷰 루트의
    // 위임 핸들러가 처리한 **뒤에** document 까지 버블링해 온다.
    //
    // 그래도 팝오버 자신과 여는 앵커 셋은 명시적으로 제외한다 —
    // 🔍 를 눌러 연 그 클릭이 곧바로 다시 닫는 것을 막아야 한다.
    // ★ closest() 가 아니라 composedPath() 로 판정한다.
    //
    //   팝오버 **안**의 버튼(예: '품목 등록')을 누르면 Sola 위임 핸들러가 먼저
    //   돌면서 그 리전을 다시 그린다. 그러면 방금 누른 노드가 DOM 에서 분리되고,
    //   그 뒤에 실행되는 이 리스너가 closest() 를 부르면 분리된 트리를 거슬러
    //   올라가느라 팝오버를 못 찾는다 → '바깥 클릭' 으로 오판해 닫아 버린다.
    //   (품목 등록 폼이 열리자마자 닫혀 아무 일도 안 일어난 것처럼 보이던 원인)
    //
    //   composedPath() 는 **dispatch 시점**에 확정된 조상 목록이라 그 사이의
    //   DOM 변경에 영향받지 않는다.
    const OPENERS = '[data-action="row-search-open"],'
                  + '[data-action="base-search-open"],'
                  + '[data-action="blank-search"]';
    this._outsideClick = (e: MouseEvent) => {
      if (this.data.rowSearch.rowKey === null) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const inside = path.some((n) => n instanceof HTMLElement
        && (n.classList.contains('svrcfg-searchpanel') || n.matches(OPENERS)));
      if (inside) return;
      // composedPath 를 못 쓰는 환경 대비 — 이때는 예전 방식으로 한 번 더 본다.
      if (path.length === 0) {
        const t = e.target as HTMLElement | null;
        if (!t || t.closest('.svrcfg-searchpanel') || t.closest(OPENERS)) return;
      }
      this.data.rowSearch = emptyRowSearch();
      this.rerenderSearchPanel();
    };
    document.addEventListener('click', this._outsideClick);
  }

  override onUnmount(): void {
    if (this._keydown) window.removeEventListener('keydown', this._keydown);
    this._keydown = null;
    if (this._reanchor) {
      window.removeEventListener('scroll', this._reanchor, true);
      window.removeEventListener('resize', this._reanchor);
    }
    this._reanchor = null;
    if (this._outsideClick) document.removeEventListener('click', this._outsideClick);
    this._outsideClick = null;
  }

  private _keydown: ((e: KeyboardEvent) => void) | null = null;
  private _outsideClick: ((e: MouseEvent) => void) | null = null;
  private _reanchor: (() => void) | null = null;
  private _reanchorTicking = false;

  /**
   * Esc 로 닫을 최상위 레이어 하나. 닫았으면 true.
   *
   * 저장 중이면 닫지 않는다. 최초 Chassis 선택 모달(replace=false)도 닫지 않는다 —
   * Chassis 없이 편집을 이어갈 수 없어 취소 자체가 불가능한 화면이다.
   */
  /**
   * 품목 검색 팝오버를 닫는다(열려 있을 때만).
   *
   * 모달을 여는 자리마다 불러야 한다. 팝오버는 z-index 1100 이고 모달은 1000
   * 이라 **항상 모달 위에** 그려진다. 그 층위는 의도된 것이다 — 기본 구성
   * 모달이 같은 팝오버를 공유하기 때문이다(base-search-open). 그래서 z-index
   * 를 낮추는 것은 답이 아니고, 팝오버를 쓰지 않는 모달을 열 때 명시적으로
   * 닫아야 한다.
   *
   * ★ 바깥 클릭 리스너로는 못 잡는다. '후보 관리' 버튼은 <summary> 안에 있어
   *   아코디언 토글을 막으려고 e.stopPropagation() 을 부르고, 그러면 그 클릭이
   *   document 까지 올라오지 않는다.
   */
  private closeRowSearch(): void {
    if (this.data.rowSearch.rowKey === null) return;
    this.data.rowSearch = emptyRowSearch();
    this.rerenderSearchPanel();
  }

  private closeTopLayer(): boolean {
    const d = this.data;
    if (d.rowSearch.rowKey !== null) {   // 검색 팝오버가 가장 위
      this.data.rowSearch = emptyRowSearch();
      this.rerenderSearchPanel();
      return true;
    }
    if (d.baseModal.open)      { if (d.baseModal.saving) return false; this.handleBaseCancel(); return true; }
    if (d.candidateModal.open) { if (d.candidateModal.saving) return false; this.handleCandCancel(); return true; }
    if (d.chassisModal.open && d.chassisModal.replace) { this.handleChassisCancel(); return true; }
    return false;
  }

  override getTitle(): string {
    return `${serverConfigTitle(this.data)} — HCLTech`;
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <article class="svrcfg-page">
        <div class="svrcfg-header-wrap">${renderHeader(this.data)}</div>
        <!-- 컨디션 교체 배너 — 표 밖 리전. rerenderBody() 가 도는 8곳마다
             다시 그려지면 배너가 깜빡이고, 표 안에 두면 스크롤에 묻힌다. -->
        <div class="svrcfg-swap-wrap">${renderBaseBanners(this.data)}</div>
        <div class="svrcfg-grid-wrap">${renderGrid(this.data)}</div>
        <div class="svrcfg-cand-wrap">${renderCandidates(this.data)}</div>
        <div class="svrcfg-modal-wrap">${renderChassisModal(this.data)}</div>
        <div class="svrcfg-candmodal-wrap">${renderCandidateModal(this.data)}</div>
        <div class="svrcfg-basemodal-wrap">${renderBaseModal(this.data)}</div>
        <!-- 품목 검색 팝오버는 **표 밖 최상위 리전**이다.
             표(.svrcfg-grid-wrap) 안에 두면 rerenderBody() 가 도는 8곳
             (카테고리 변경·행 삭제·후보 추가·기본구성 적용 …)과 비동기
             fillSpecAndShorthand 가 끝날 때마다 열려 있는 팝오버가 새 DOM 으로
             다시 만들어져 입력 포커스와 caret 이 날아간다.
             position:fixed + z-index:1100 이라 DOM 위치는 화면에 영향이 없다. -->
        <div class="svrcfg-searchpanel-wrap">${renderRowSearchPanel(this.data)}</div>
      </article>
    `;
  }

  // ─── 행 조회 헬퍼 ─────────────────────────────────────────────────
  private rowOf(el: HTMLElement): ConfigRow | undefined {
    const key = el.dataset.rowKey ?? el.closest<HTMLElement>('[data-row-key]')?.dataset.rowKey;
    return key ? this.data.rows.find((r) => r.key === key) : undefined;
  }

  // ─── 소스 후보 (재고 + RFQ) ───────────────────────────────────────
  /**
   * 구성에 올라온 파트들의 소스 후보를 한 번에 불러온다.
   *
   * 재고: GET /server-config/stock (파트 목록 일괄 — 행마다 요청하지 않는다)
   * RFQ : 기존 GET /rfq/deal-sources?deal_id= 재사용 (딜 단위 1회)
   * 품목이 추가/교체될 때마다 다시 부른다(새 파트의 재고가 필요하므로).
   */
  private async loadSources(): Promise<void> {
    // 구성 표 + **후보 목록**의 파트를 한 번에 묻는다.
    //   후보 행에도 소스 드롭다운이 있고(담기 전에 골라두는 자리), 그 파트를
    //   빼면 옵션이 비어 '후보 없음'만 뜬다.
    const parts = [
      ...this.data.rows.map((r) => r.optionPart),
      ...this.data.candidates.groups.flatMap((g) => g.items.map((i) => i.option_part)),
    ].filter(Boolean);
    this.data.sources = { ...this.data.sources, loading: true, error: '' };
    this.rerenderSourceCells();
    const seq = ++this.sourcesSeq;
    try {
      const res = await api.fetchSourceCandidates(parts, this.data.dealId);
      if (seq !== this.sourcesSeq) return;
      this.data.sources = { loading: false, error: '', stock: res.stock, rfq: res.rfq };
    } catch (err) {
      if (seq !== this.sourcesSeq) return;
      this.data.sources = {
        ...this.data.sources, loading: false,
        error: err instanceof Error ? err.message : '소스 후보를 불러오지 못했습니다.',
      };
    }
    this.rerenderSourceCells();
  }

  /**
   * 소스 select 만 다시 그린다 — 표 전체를 건드리지 않는다(다른 input 포커스 보호).
   * 후보 목록의 소스 select 도 같은 데이터를 쓰므로 함께 갱신한다.
   */
  private rerenderSourceCells(): void {
    for (const row of this.data.rows) {
      this.refreshRegion(
        `.svrcfg-row[data-row-key="${row.key}"] .svrcfg-c-src`,
        () => renderSourceSelect(this.data, row),
      );
    }
    // 후보 섹션에는 입력 중인 필드가 없어 통째로 다시 그려도 안전하다.
    if (this.data.candidates.groups.length > 0) this.rerenderCandidates();
  }

  // ─── 약식 디스크립션 (AUTO) ───────────────────────────────────────
  /**
   * 카탈로그 스펙으로 약식 디스크립션을 만들어 행에 반영한다.
   *
   * 수기 수정한 행(descriptionManual)은 건드리지 않는다 — MANUAL_OVERRIDE 는
   * 권위값이고 AUTO 파서가 재작성하지 않는다는 규칙(shared shorthand).
   * 스펙이 없거나 필수 필드가 빠지면 생성기가 카탈로그 원문을 그대로 돌려준다.
   */
  private applyShorthand(row: ConfigRow): void {
    if (row.descriptionManual) return;
    if (!row.specCategory) return;
    const text = buildLineShorthand({
      category:       row.specCategory,
      quantity:       row.quantity,
      ...(row.spec ? { spec: row.spec } : {}),
      descriptionRaw: row.descriptionRaw,
      chassisModel:   (chassisRow(this.data)?.spec?.['model'] as string | undefined) ?? null,
    });
    if (text) row.description = text;
  }

  /**
   * 카탈로그 full row 를 받아 스펙을 채우고 약식을 다시 만든다.
   *
   * 전역 검색 결과(extra 컬럼)에는 스펙이 부족하다(memory 는 description 만) —
   * 그래서 품목이 정해진 뒤 한 번 full row 를 조회한다.
   */
  /**
   * 카탈로그 full row 를 받아 **스펙만** 채운다. 행 문구는 건드리지 않는다.
   *
   * 왜 갈랐나 (2026-08-21):
   *   87b86e9(08-12)에서 '수량만 고쳤는데 원문이 약식으로 바뀐다'는 제보에
   *   handleQtyInput 이 부르던 refreshShorthand 를 뺐다. 그런데 그 경로는
   *   두 가지를 한다 — 스펙을 채우는 것과 행 문구를 다시 쓰는 것. 문제였던
   *   건 뒤쪽인데 앞쪽까지 같이 꺼졌다. 그래서 수정 모드에서는 스펙이 영영
   *   안 채워졌고, 수량을 고쳐도 약식이 다시 안 만들어졌으며, 같은 파트넘버를
   *   합쳐도 개수·총 용량을 다시 계산할 수 없었다(서안드레 'POWER : 800W *1EA').
   *
   * 채웠으면 true. 부르는 쪽이 다시 그릴지 정한다.
   */
  private async fetchSpec(row: ConfigRow): Promise<boolean> {
    if (row.spec) return false;                    // 이미 있으면 조회하지 않는다
    if (!row.catalogSource || !row.optionPart) return false;
    // 비하드웨어는 스펙 조합이 없다 — 약식 정책이 원문 그대로다.
    // ADMIN_TABLES 에도 없어 조회하면 404 가 날 뿐이므로 부르지 않는다.
    if (row.catalogSource === 'non_hw') return false;
    try {
      const full = await api.fetchCatalogRow(row.catalogSource, row.optionPart);
      if (!full) return false;
      row.spec = full;
      if (!row.descriptionRaw) {
        row.descriptionRaw = (full['description_raw'] as string | undefined)
          ?? (full['description'] as string | undefined) ?? null;
      }
      return true;
    } catch {
      // 조회 실패는 치명적이지 않다 — 원문/기존 문구를 유지한다.
      return false;
    }
  }

  private async fillSpecAndShorthand(row: ConfigRow): Promise<void> {
    if (!(await this.fetchSpec(row))) return;
    try {
      this.applyShorthand(row);
      // 구성 표와 헤더(합계)만 — 후보 섹션은 건드리지 않는다.
      //   이 함수는 비동기라, 후보 섹션을 다시 그리면 사용자가 그 사이에 입력한
      //   그룹 검색어의 포커스를 뒤늦게 날려버린다.
      this.refreshRegion('.svrcfg-grid-wrap', () => renderGrid(this.data));
      this.rerenderHeader();
    } catch {
      // 다시 그리다 실패해도 값은 이미 행에 들어가 있다 — 저장을 막지 않는다.
    }
  }

  /**
   * 수량 등이 바뀌어 약식을 다시 만들어야 할 때. 스펙이 없으면 먼저 조회한다
   * (수정 모드로 열면 스펙이 비어 있으므로 lazy 로 채운다).
   */
  private refreshShorthand(row: ConfigRow): void {
    if (row.descriptionManual) return;
    if (row.spec) { this.applyShorthand(row); return; }
    void this.fillSpecAndShorthand(row);
  }

  /**
   * 화면을 열 때 스펙이 빈 행을 한 번에 채운다 — **행 문구는 안 건드린다**.
   *
   * 저장된 구성은 rowFromDealItem 이 spec: null 로 만든다(deal_item 에 스펙을
   * 저장하지 않으므로 채울 값이 없다). 기본 구성 행도 applyBaseItems 가
   * catalogSource 만 유도하고 조회는 하지 않아 비어 있다. 그 상태로 두면
   * 수정 모드가 생성 모드와 다른 로직을 타게 된다 — 수량을 고쳐도 약식이
   * 안 만들어지고, 같은 파트넘버를 합쳐도 개수·총 용량을 못 세운다.
   *
   * 여기서 채워 두면 그 뒤로는 두 모드가 같은 길을 간다.
   *
   * 수기 수정 행(MANUAL_OVERRIDE)도 채운다 — 스펙은 그 행의 문구를 바꾸지
   * 않는다. applyShorthand 가 먼저 그 플래그를 보고 빠진다.
   *
   * 실패는 무시한다. 조회가 안 되면 지금까지처럼 저장된 문구를 그대로 쓴다.
   */
  private async fillMissingSpecs(): Promise<void> {
    const targets = this.data.rows.filter((r) => !r.spec && !!r.catalogSource
      && r.catalogSource !== 'non_hw' && !!r.optionPart);
    if (targets.length === 0) return;
    const filled = await Promise.all(targets.map((r) => this.fetchSpec(r)));
    if (!filled.some(Boolean)) return;
    // 표에 보이는 값(descCellValue)은 descriptionRaw 우선이라, 원문이 비어
    // 있던 행은 여기서 채워지며 표시가 바뀔 수 있다. 그래서 다시 그린다.
    this.refreshRegion('.svrcfg-grid-wrap', () => renderGrid(this.data));
    this.rerenderHeader();
  }

  // ─── 헤더 ─────────────────────────────────────────────────────────
  handleBundleInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const n = parsePrice(el.value);
    // 입력 중 3자리 콤마 유지 + caret 보정은 재렌더 없이 직접 처리.
    this.data.bundlePrice = n === null ? '' : n.toLocaleString('ko-KR');
    el.value = this.data.bundlePrice;
    // 사람이 적은 값은 자동 반영이 덮지 않는다. 칸을 비우면 다시 자동으로 돌아온다
    //   — '자동을 끄는 방법'과 '되돌리는 방법'이 같은 칸 안에 있어야 한다.
    this.data.bundleManual = n !== null;
    if (!this.data.bundleManual) this.syncBundleFromParts();
    this.rerenderPriceNotice();   // input 이 든 헤더는 다시 그리지 않는다
  }

  /**
   * 구성품 판매가 합계를 서버 판매 가격 칸에 비춘다 (2026-08-21 회의 03:36).
   *
   * '개별 파트 가격을 매기고 그 합산을 주로 쓴다' 는 실사용에 맞춘다. 사람이
   * 판매가를 직접 적은 뒤에는(bundleManual) 손대지 않는다 — 약식의
   * descriptionManual 과 같은 규칙이다.
   *
   * ★ 헤더 전체를 다시 그리지 않고 input 의 value 만 바꾼다. 이 함수는 구성품
   *   판매가·수량을 입력하는 도중에 불리므로, 헤더를 다시 그리면 그 입력칸이
   *   아니라 **헤더 안의 다른 입력칸** 이 교체되어 IME 조합이 끊긴다.
   */
  private syncBundleFromParts(): void {
    if (this.data.bundleManual) return;
    const sum = mirroredBundle(this.data.rows);
    // 비출 값이 없으면(구성품 판매가가 하나도 없음) 칸을 건드리지 않는다 —
    //   지우면 사람이 아직 보지 못한 사이에 값이 사라진다.
    if (sum === null) return;
    const text = sum.toLocaleString('ko-KR');
    if (text === this.data.bundlePrice) return;
    this.data.bundlePrice = text;
    const el = document.querySelector<HTMLInputElement>('[data-action="bundle-input"]');
    if (el) el.value = text;
  }

  /**
   * 오늘 환율 — 매입단가가 USD 인 행을 원화로 환산해 마진을 내는 데 쓴다.
   *
   * 실패해도 화면을 막지 않는다. 환율이 없으면 외화 행만 합계에서 빠지고 그
   * 건수를 표 아래에 적는다(renderTotals). 견적 모달과 같은 태도다 —
   * 환율 조회 실패가 작업 불가를 뜻하지는 않는다.
   */
  private async loadFxRate(): Promise<void> {
    try {
      const fx = await api.fetchTodayExchangeRate();
      if (this.data.fxRate.trim() !== '') return;
      this.data.fxRate = String(fx.rate_krw_per_usd);
      this.rerenderHeader();
      this.rerenderBody();   // 행 마진도 환율을 쓴다
    } catch { /* 환율 없이 진행 — 외화 행만 합계에서 빠진다 */ }
  }

  /** 구성품 판매가·수량이 바뀐 뒤 — 판매가 반영 + 마진 셀 + 합계/경고. */
  private pricesChanged(row?: ConfigRow): void {
    this.syncBundleFromParts();
    if (row) this.rerenderMarginCells(row);
    this.rerenderPriceNotice();
  }

  /**
   * 그 행의 마진금액·마진율 칸만 다시 쓴다.
   *
   * 표를 다시 그리지 않는다 — 이 함수는 매입단가·판매단가·수량을 **입력하는
   * 도중에** 불리므로, 행을 다시 그리면 지금 타이핑 중인 input 이 교체된다.
   * 표시 문자열은 표와 같은 함수(rowMarginText/rowRateText)로 만든다.
   */
  private rerenderMarginCells(row: ConfigRow): void {
    const tr = document.querySelector(`tr[data-row-key="${CSS.escape(row.key)}"]`);
    if (!tr) return;
    const m = tr.querySelector('.svrcfg-c-margin');
    const p = tr.querySelector('.svrcfg-c-rate');
    const fx = parseFx(this.data.fxRate);
    if (m) m.textContent = rowMarginText(row, fx);
    if (p) p.textContent = rowRateText(row, fx);
  }

  /**
   * 저장된 번들가가 '사람이 직접 적은 값' 인가 — 화면을 열 때 한 번 판정한다.
   *
   * 이 플래그는 저장하지 않는다(types.ts 주석). 판정 규칙은 두 줄이다.
   *   · 번들가가 없다        → 자동. 합계가 완성되면 그때 채운다.
   *   · 번들가 = 구성품 합계 → 자동. 어느 쪽이든 화면에 같은 숫자가 남는다.
   *   · 그 밖(다르다)       → 수동. 저장된 값을 지키고 불일치를 표시한다.
   * 합계를 낼 수 없는 상태(구성품 판매가 일부만 입력)도 '다르다' 쪽으로 본다 —
   * 비출 값이 없는데 저장된 값을 지울 이유가 없다.
   */
  private static bundleWasManual(stored: number | null, rows: readonly ConfigRow[]): boolean {
    if (stored === null) return false;
    return stored !== mirroredBundle(rows);
  }

  // ─── grid 편집 ────────────────────────────────────────────────────
  handleCategorySelect(e: Event): void {
    const el = e.target as HTMLSelectElement;
    const row = this.rowOf(el);
    if (!row) return;
    row.specCategory = el.value as SpecCategory | '';
    this.refreshShorthand(row);   // 카테고리별 공식이 다르다
    this.sortRows();
    this.rerenderBody();
  }

  handleDescInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const row = this.rowOf(el);
    if (!row) return;
    row.description = el.value;
    row.descriptionManual = true;   // 수기 수정 — AUTO 가 재작성하지 않는다.
  }

  handleQtyInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const row = this.rowOf(el);
    if (!row) return;
    // 기본 구성 품목의 수량은 기본 구성이 정한다(마이그 174). input 은 disabled 라
    // 여기까지 오지 않지만, 위임 핸들러는 DOM 을 믿지 않는다.
    if (row.isBasePart) { el.value = String(row.quantity); return; }
    const n = Math.max(1, Math.trunc(Number(el.value) || 1));
    row.quantity = n;
    // 약식은 수량에 따라 문자열이 달라진다(*8EA, 128GB, *2P) → 재생성한다.
    //
    // 2026-08-21 — 87b86e9 이 여기를 `if (row.spec)` 로 좁혔던 이유는 그때
    //   스펙 없는 행에서 refreshShorthand 가 카탈로그를 lazy 조회하며 원문을
    //   약식으로 갈아엎었기 때문이다. 이제 조회는 화면을 열 때 fillMissingSpecs
    //   가 미리 끝내고(행 문구는 안 건드린다), 여기서는 조회가 일어나지 않는다.
    //   그래서 원래 하려던 대로 재생성한다 — 수량이 2인데 '*1EA' 로 남아 있으면
    //   그게 거짓말이고, 그 문구가 그대로 고객 문서로 나간다.
    //   수기 수정 행은 applyShorthand 첫 줄이 막는다.
    this.applyShorthand(row);
    this.pricesChanged(row);      // 합계·경고 + 판매가 반영 + 마진 셀
    this.rerenderDescCell(row);
  }

  handlePriceInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const row = this.rowOf(el);
    if (!row) return;
    const n = parsePrice(el.value);
    row.salePrice = n;
    el.value = n === null ? '' : n.toLocaleString('ko-KR');
    this.pricesChanged(row);
  }

  /**
   * 매입단가 직접 입력 (2026-08-21 기획 '모든 데이터 입력 optional').
   *
   * ★ 소스 스냅샷과 **같은 칸**을 쓴다. 소스를 고르면 그 값이 이 칸에 들어오고,
   *   여기서 적으면 그 값을 덮는다. 반대로 나중에 소스를 고르면 적어둔 값이
   *   다시 덮인다 — 지금은 둘을 구분할 자리가 없다(source_type 에 수동 개념이
   *   없고 cost_basis_amount 도 하나뿐이다). 구분이 필요해지면 그때 스키마를
   *   늘린다. 여기서 임의로 새 컬럼을 만들지 않는다.
   *
   * 통화는 원화로 둔다. 손으로 적는 값은 원화라는 것이 이 화면의 전제이고,
   * 외화는 소스 스냅샷으로만 들어온다.
   */
  handleCostInput(e: Event): void {
    const el  = e.target as HTMLInputElement;
    const row = this.rowOf(el);
    if (!row) return;
    // 기본 구성 품목의 매입원가는 섀시 값에 포함돼 0 고정이다(마이그 174).
    if (row.isBasePart) { el.value = ''; return; }
    const n = parsePrice(el.value);
    // 이 칸은 **원화**다. 직접 적으면 원화 원가로 잡고 소스를 해제한다 —
    //   견적 모달과 같은 규칙(handleQuoteRowChange, field costKrw). 소스가
    //   준 값과 사람이 적은 값이 한 칸에 같이 있으면 나중에 소스를 다시 고를 때
    //   어느 쪽이 맞는지 판단할 근거가 사라진다. 칸의 주인은 하나여야 한다.
    row.costAmount   = n;
    row.costCurrency = n === null ? null : 'KRW';
    row.sourceType   = null;
    row.sourceRefId  = null;
    row.sourceLabel  = null;
    el.value = n === null ? '' : n.toLocaleString('ko-KR');
    el.title = nativeCostTitle(row);
    this.rerenderSourceCells();   // 소스 해제를 select 에 반영
    this.rerenderMarginCells(row);
    this.rerenderPriceNotice();   // 합계만 — 이 input 은 건드리지 않는다
  }

  /**
   * 소스 선택 — 스냅샷(type/id/label/cost)을 행에 보존한다.
   *
   * 원가가 없는 후보를 골라도 0 으로 치환하지 않는다(핸드오프: '확인 필요' 와
   * 빈 cost 로 보관). 라벨도 함께 저장해 원천이 바뀌어도 당시 표기를 유지한다.
   */
  handleSourceSelect(e: Event): void {
    const el = e.target as HTMLSelectElement;
    const row = this.rowOf(el);
    if (!row) return;
    // 기본 구성 품목은 매입 소스를 고르지 않는다 — 원가가 섀시 값에 포함돼
    // 0 으로 고정이다(마이그 174). select 는 disabled 지만 여기서도 막는다.
    if (row.isBasePart) return;
    this.applySourceValue(row, el.value);
  }

  /**
   * select value → 행의 소스 스냅샷.
   *
   * 구성 표에서 직접 고른 경우와, 후보 행에서 미리 골라둔 값을 `추가` 때
   * 반영하는 경우가 같은 경로를 쓴다(후자는 DOM 이 이미 사라졌을 수 있으므로
   * 라벨을 DOM 이 아니라 데이터에서 만든다).
   */
  private applySourceValue(row: ConfigRow, value: string): void {
    // 스냅샷 조립은 lib/source-options 가 정본이다 — 딜 상세의 구성품 행도
    // 같은 함수를 쓰므로 두 화면이 같은 라벨·원가를 저장한다.
    const snap = sourceSnapshot(this.data.sources, row.optionPart, value);
    row.sourceType   = snap.sourceType;
    row.sourceRefId  = snap.sourceRefId;
    row.sourceLabel  = snap.sourceLabel;
    row.costAmount   = snap.costAmount;
    row.costCurrency = snap.costCurrency;
    // ★ 매입단가 칸·마진·합계를 같이 갱신한다 (2026-08-21).
    //   소스를 고르면 그 스냅샷의 원가가 행에 들어오는데, 예전에는 화면에
    //   매입단가 열이 없어 아무것도 다시 그릴 필요가 없었다. 열이 생긴 뒤로는
    //   state 만 바뀌고 칸은 빈 채로 남아 '소스를 골랐는데 숫자가 안 들어온다'
    //   가 된다. 후보 목록에서 미리 골라둔 값을 반영하는 경로도 같이 지난다.
    this.rerenderCostCell(row);
    this.rerenderMarginCells(row);
    this.rerenderPriceNotice();
  }

  /**
   * 그 행의 매입단가 칸만 다시 쓴다 — 값과 외화 배지.
   *
   * 표를 다시 그리지 않는다(다른 행의 입력 포커스 보호). 소스 select 를
   * 조작하는 중이라 이 칸 자체에는 커서가 없다.
   */
  private rerenderCostCell(row: ConfigRow): void {
    const el = document.querySelector<HTMLInputElement>(
      `[data-action="cost-input"][data-row-key="${row.key}"]`,
    );
    if (!el) return;
    // 칸에 들어가는 값은 **원화 환산 후**다 — 표를 그릴 때와 같은 함수를 쓴다.
    const krw   = costKrw(row, parseFx(this.data.fxRate));
    const shown = krw === null ? '' : krw.toLocaleString('ko-KR');
    if (el.value !== shown) el.value = shown;
    el.title = nativeCostTitle(row);
  }

  handleRowDelete(e: Event): void {
    const row = this.rowOf(e.target as HTMLElement);
    if (!row) return;
    this.data.rows = this.data.rows.filter((r) => r.key !== row.key);
    this.syncBundleFromParts();   // 구성품이 빠지면 합계가 달라진다
    this.rerenderBody();
  }

  /**
   * 기본 품목 복제 — 같은 부품을 한 줄 더 담는다(2026-08-20 회의 08:01).
   *
   * ★ 복제본은 **기본 품목이 아니다.** 이게 이 기능의 핵심이다.
   *   기본 행(마이그 174)은 수량이 잠기고 소스를 못 고르며 매입원가가 0이다 —
   *   섀시 값에 이미 포함된 물건이기 때문이다. 복제본까지 그 성질을 물려받으면
   *   수량도 못 고치고 원가도 0이라, "배터리를 하나 더 산다" 는 목적 자체가
   *   성립하지 않는다. 복제본은 사람이 직접 담은 일반 행이어야 한다.
   *
   *   그래서 기본 성질에 딸린 값들을 전부 초기화한다:
   *     isBasePart=false  → `기본` 뱃지 사라짐, 수량 input·소스 select 잠금 해제
   *     quantity=1        → 기본 구성이 정한 수량을 물려받지 않는다
   *     salePrice=null    → 원본은 섀시에 포함돼 비어 있고, 복제본은 따로 매긴다
   *     소스·원가 스냅샷   → 기본 행은 애초에 비어 있지만 명시적으로 지운다
   *
   * ★ `id` 를 떼어낸다. 남기면 저장 때 원본 deal_item 을 가리켜, 한 줄이
   *   늘어나는 대신 원본이 수정된다.
   *
   * 나머지(카테고리·파트넘버·디스크립션·카탈로그 스펙)는 그대로 복사한다 —
   * 같은 부품이므로 다시 검색하게 만들 이유가 없다.
   */
  handleRowDuplicate(e: Event): void {
    const row = this.rowOf(e.target as HTMLElement);
    // 버튼은 기본 행에만 그리지만, 위임 클릭이라 대상을 한 번 더 확인한다.
    if (!row || !row.isBasePart) return;

    const idx = this.data.rows.findIndex((r) => r.key === row.key);
    if (idx < 0) return;

    // 같은 파트의 일반 행이 이미 있으면 새 행 대신 그 행 수량을 더한다
    //   (카탈로그/임시등록 담기와 같은 규칙). 복제는 '같은 부품을 하나 더'
    //   담는 것이라 결과가 같아야 한다 — 기본 행 옆에 일반 행이 이미 있으면
    //   그게 곧 '하나 더'의 자리다.
    const merged = this.mergeIntoExisting(row.specCategory, row.optionPart ?? '', 1);
    if (merged) {
      this.applyShorthand(merged);   // 수량이 바뀌었으니 약식 재계산
      this.rerenderBody();
      return;
    }

    const { id: _originalId, ...rest } = row;
    const copy: ConfigRow = {
      ...rest,
      key:          newRowKey(),
      isBasePart:   false,
      quantity:     1,
      salePrice:    null,
      sourceType:   null,
      sourceRefId:  null,
      sourceLabel:  null,
      costAmount:   null,
      costCurrency: null,
    };

    // 원본 바로 뒤에 끼운다. sortRows() 는 카테고리로 묶고 같은 카테고리
    // 안에서는 배열 순서를 지키므로, 복제본이 원본 옆에 남는다.
    this.data.rows.splice(idx + 1, 0, copy);
    this.sortRows();
    this.rerenderBody();
  }

  // ─── 품목 검색 (blank row) ────────────────────────────────────────
  handleBlankSearch(e: Event): void {
    const el = e.target as HTMLInputElement;
    const v = el.value;
    if (this.rowSearchTimer) clearTimeout(this.rowSearchTimer);

    // 입력을 비우면 닫는다. 예전엔 팝오버가 "입력하세요" 힌트를 띄운 채 남았다.
    if (v.trim() === '') {
      this.data.rowSearch = emptyRowSearch();
      this.rerenderSearchPanel();
      return;
    }

    this.data.rowSearch = {
      ...this.data.rowSearch, rowKey: '__blank__', query: v,
      ...ServerConfigView.anchorOf(el),
    };
    // 패널을 **즉시** 띄운다. 예전엔 여기서 상태만 세우고 그리지 않아,
    // 디바운스(250ms) 뒤 runSearch 가 돌 때까지 화면에 아무것도 없었다.
    // blank 경로의 검색 input 은 표 안에 있어서 패널을 다시 그려도
    // 포커스·caret 을 잃지 않는다(팝오버가 input 을 갖는 건 ✎ 경로뿐).
    this.rerenderSearchPanel();
    this.rowSearchTimer = setTimeout(
      () => { void this.runSearch(v); },
      ServerConfigView.SEARCH_DEBOUNCE_MS,
    );
  }

  handleRowSearchOpen(e: Event): void {
    const target = e.target as HTMLElement;
    const row = this.rowOf(target);
    if (!row) return;
    // 기본 구성 품목은 품목 검색을 열지 않는다(버튼도 없지만 위임 핸들러는
    //   DOM 을 믿지 않는다). 품목 교체는 ⚙ 의 기본 구성 편집으로만 한다.
    if (row.isBasePart) return;
    const cell = target.closest('td') ?? target;
    this.data.rowSearch = {
      ...emptyRowSearch(), rowKey: row.key,
      ...ServerConfigView.anchorOf(cell as HTMLElement),
    };
    this.rerenderSearchPanel();
    this.focusPanelInput();
  }

  /** 팝오버 안의 검색 — 결과 리전만 갱신해 input 의 caret 을 지킨다. */
  handlePanelSearch(e: Event): void {
    const el = e.target as HTMLInputElement;
    this.data.rowSearch = { ...this.data.rowSearch, query: el.value };
    const v = el.value;
    if (this.rowSearchTimer) clearTimeout(this.rowSearchTimer);
    this.rowSearchTimer = setTimeout(
      () => { void this.runSearch(v); },
      ServerConfigView.SEARCH_DEBOUNCE_MS,
    );
  }

  /**
   * 팝오버를 버튼으로 열면 포커스가 그 버튼에 남는다 — 바로 타이핑할 수 있게
   * input 으로 옮긴다. 렌더 직후라 다음 프레임에 잡는다.
   */
  private focusPanelInput(): void {
    requestAnimationFrame(() => {
      document.querySelector<HTMLInputElement>('[data-action="panel-search"]')?.focus();
    });
  }

  /** 앵커 요소 기준 팝오버 좌표(viewport 기준 — position: fixed 용). */
  private static anchorOf(el: HTMLElement): { x: number; y: number; width: number } {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.bottom + 4, width: r.width };
  }

  /**
   * 열려 있는 팝오버의 앵커 요소를 DOM 에서 **다시 찾는다**.
   *
   * 여는 시점의 요소를 필드에 들고 있지 않는다 — 행을 다시 그리면 그 노드는
   * 문서에서 떨어지고, 떨어진 노드의 getBoundingClientRect() 는 전부 0 이라
   * 팝오버가 좌상단으로 튄다. 여는 세 경로와 **같은 요소**를 고른다.
   */
  private anchorElNow(): HTMLElement | null {
    const key = this.data.rowSearch.rowKey;
    if (key === null) return null;
    // blank 는 표 안 input 자체가 앵커다(td 가 아니다 — handleBlankSearch 와 같아야 한다).
    if (key === '__blank__') {
      return document.querySelector<HTMLElement>('[data-action="blank-search"]');
    }
    // ✎(표) / 품목 선택(기본 구성 모달) 은 버튼이 든 td 가 앵커다.
    const k = CSS.escape(key);
    const btn = document.querySelector<HTMLElement>(
      `[data-action="row-search-open"][data-row-key="${k}"],`
      + `[data-action="base-search-open"][data-row-key="${k}"]`,
    );
    return btn ? (btn.closest('td') ?? btn) : null;
  }

  /**
   * 스크롤/리사이즈 때 팝오버를 앵커에 다시 붙인다.
   *
   * ★ 다시 그리지 않고 style 만 직접 쓴다 — 팝오버가 검색 input 을 품고 있어
   *   리렌더하면 입력 중 caret 이 날아간다(runSearch 주석과 같은 함정).
   *   상태의 x/y 도 같이 갱신해, 이후 전체 리렌더가 옛 좌표로 되돌리지 않게 한다.
   *
   * min-width 는 갱신하지 않는다 — 계산식(Math.max(width, 420))이 render.ts 에
   * 있어 여기서 다시 쓰면 상수가 두 군데로 갈라진다. 스크롤은 폭을 바꾸지 않는다.
   */
  private repositionSearchPanel(): void {
    if (this.data.rowSearch.rowKey === null) return;
    if (this._reanchorTicking) return;   // scroll 마다 강제 레이아웃 → rAF 로 한 번만
    this._reanchorTicking = true;
    requestAnimationFrame(() => {
      this._reanchorTicking = false;
      if (this.data.rowSearch.rowKey === null) return;
      const anchor = this.anchorElNow();
      const panel  = document.querySelector<HTMLElement>('.svrcfg-searchpanel');
      // 못 찾으면 그냥 둔다 — 0,0 으로 튀게 만드는 것보다 제자리가 낫다.
      if (!anchor || !panel) return;
      const a = ServerConfigView.anchorOf(anchor);
      this.data.rowSearch = { ...this.data.rowSearch, ...a };
      panel.style.left = `${Math.round(a.x)}px`;
      panel.style.top  = `${Math.round(a.y)}px`;
    });
  }

  /**
   * 팝오버의 '품목 등록' — 검색어를 파트넘버로 삼아 행을 만든다.
   *
   * canonical 카탈로그를 바꾸지 않는다. 저장 시 백엔드가 카탈로그 부재를 보고
   * is_unregistered 를 자동 판정해 '임시' 로 표시된다. 카테고리는 사용자가
   * 직접 지정해야 하므로 비워 두고(=경고 표시), 완료 검증이 이를 막는다.
   */
  /**
   * '품목 등록' 버튼 — 폼을 편다(예전엔 여기서 바로 등록이 끝났다).
   *
   * 검색어를 파트넘버 칸에 미리 채운다. 카탈로그에 없어서 등록하는 것이니
   * 방금 친 문자열이 곧 파트넘버인 경우가 대부분이다.
   */
  handleRowRegister(): void {
    const s = this.data.rowSearch;
    if (s.rowKey === null) return;
    this.data.rowSearch = {
      ...s,
      register: {
        ...emptyRowRegister(),
        open: true,
        part: s.query.trim().toUpperCase(),
      },
    };
    this.rerenderSearchBody();
  }

  /** 카테고리 select — 상태만 바꾼다(select 가 이미 그 값을 보여준다). */
  handleRowRegCategory(_e: Event, target: HTMLElement): void {
    this.data.rowSearch.register.category =
      (target as HTMLSelectElement).value as SpecCategory | '';
  }

  // 아래 둘은 **다시 그리지 않는다** — 팝오버 안의 input 이라 재렌더하면
  // caret 이 날아가 한 글자만 쳐진다(표 검색에서 겪었던 함정과 같다).
  handleRowRegPart(_e: Event, target: HTMLElement): void {
    this.data.rowSearch.register.part = (target as HTMLInputElement).value;
  }

  handleRowRegDesc(_e: Event, target: HTMLElement): void {
    this.data.rowSearch.register.desc = (target as HTMLInputElement).value;
  }

  /** 폼만 닫는다 — 팝오버와 검색 결과는 그대로 둔다. */
  handleRowRegisterCancel(): void {
    this.data.rowSearch = { ...this.data.rowSearch, register: emptyRowRegister() };
    this.rerenderSearchBody();
  }

  /**
   * '등록 및 선택' — 카탈로그를 바꾸지 않고 이 구성에만 임시 품목으로 넣는다.
   *
   * 카테고리·파트넘버·디스크립션 셋 다 필수다(프로토타입 규격). 파트넘버는
   * 대문자로 저장한다 — 카탈로그 PN 표기와 맞춘다.
   */
  handleRowRegisterSave(): void {
    const s = this.data.rowSearch;
    const g = s.register;
    const category = g.category;
    const part     = g.part.trim().toUpperCase();
    const desc     = g.desc.trim();

    if (!category || !part || !desc) {
      this.data.rowSearch = {
        ...s,
        register: { ...g, error: '카테고리, 파트넘버, 디스크립션을 입력해 주세요.' },
      };
      this.rerenderSearchBody();
      return;
    }

    const targetKey = s.rowKey;

    // 기본 구성 모달의 행이면 그쪽에 반영한다(검색 팝오버 공유).
    const baseRow = this.data.baseModal.open
      ? this.data.baseModal.items.find((r) => r.key === targetKey)
      : undefined;
    if (baseRow) {
      baseRow.specCategory      = category;
      baseRow.optionPart        = part;
      baseRow.description       = desc;
      baseRow.descriptionRaw    = desc;
      baseRow.isUnregistered    = true;
      baseRow.catalogSource     = null;
      // 사람이 직접 적은 문구다 — AUTO 약식 생성기가 덮어쓰면 안 된다.
      baseRow.descriptionManual = true;
      this.data.rowSearch = emptyRowSearch();
      this.rerenderBaseRows();
      this.rerenderSearchPanel();
      // ★ 이 분기도 인테이크에 올린다 (2026-08-19).
      //   여기서 곧장 return 하는 바람에 함수 끝의 registerPending 에 닿지 못했고,
      //   기본 구성에서 등록한 파트는 카탈로그에도 인테이크에도 없는 미아가 됐다.
      //   딜 행 분기는 끝까지 흘러가 정상 적재되던 것과 대비된다 — 같은 버튼인데
      //   어느 행에 등록했느냐로 결과가 갈렸다. prod 실측 3건(754381-001 /
      //   777285-001 / 869432-001)이 전부 이 경로로 만들어졌다.
      void this.registerPending(category, part, desc);
      return;
    }

    if (targetKey === '__blank__') {
      // 같은 파트(기본 아님)가 있으면 수량 합산. 단 임시등록은 사람이
      //   디스크립션을 직접 적은 행(descriptionManual)이라 mergeIntoExisting
      //   이 그런 행을 제외하므로, 합쳐지는 것은 카탈로그로 담긴 같은 파트뿐이다.
      const merged = this.mergeIntoExisting(category, part, 1);
      if (!merged) {
        this.data.rows.push({
          ...blankRow(),
          specCategory:      category,
          optionPart:        part,
          description:       desc,
          descriptionRaw:    desc,
          descriptionManual: true,
          isUnregistered:    true,
        });
      }
    } else {
      const row = this.data.rows.find((r) => r.key === targetKey);
      if (row) {
        row.specCategory      = category;
        row.optionPart        = part;
        row.description       = desc;
        row.descriptionRaw    = desc;
        row.descriptionManual = true;
        row.isUnregistered    = true;
        row.catalogSource     = null;
        row.spec              = null;   // 카탈로그 스펙이 없다
      }
    }
    this.data.rowSearch = emptyRowSearch();
    this.sortRows();
    this.rerenderBody();
    this.rerenderSearchPanel();
    void this.loadSources();
    void this.registerPending(category, part, desc);
  }

  /**
   * 임시등록 품목을 인테이크(catalog_part_pending)에 **즉시** 올린다.
   *
   * 서안드레 확인 — '등록 및 선택' 을 누르는 그 순간 pending 에 들어가야 한다.
   * 예전에는 이 경로가 아예 없어서, 품목 확정을 해야만(deal.service 의 확정 루프)
   * 인테이크에 올라갔다. 그래서 등록해 놓고 임시등록 페이지를 열면 아무것도
   * 없었다.
   *
   * 화면 등록은 이 호출과 **독립**이다. 네트워크가 실패해도 행은 이미 구성에
   * 들어가 있고, 나중에 품목 확정이 같은 파트를 다시 올린다(두 경로 모두
   * ON CONFLICT ... COALESCE 라 서로 덮어쓰지 않는다). 그래서 여기서 예외를
   * 던져 저장 흐름을 되돌리지 않는다 — 다만 조용히 삼키지도 않는다. 오늘
   * 겪은 것처럼 "등록했는데 목록에 없다" 가 반복되면 안 된다.
   *
   * category 는 화면 어휘(CPU/RAM/…)를 그대로 보낸다. 카탈로그 소스명으로의
   * 변환은 서버가 한다(어휘 규칙을 한 곳에만 둔다).
   */
  private async registerPending(
    category: SpecCategory | '',
    part:     string,
    desc:     string,
  ): Promise<void> {
    try {
      await api.createPendingPart({
        option_part:    part,
        description:    desc || null,
        category:       category || null,
        source_deal_id: this.data.dealId || null,
      });
    } catch (err) {
      this.fail(`임시등록 품목 적재 실패: ${err instanceof Error ? err.message : 'unknown'}`
        + ' — 구성에는 담겼습니다. 품목 확정 시 다시 시도됩니다.');
    }
  }

  /**
   * ★ 결과 리전만 갱신한다(rerenderSearchPanel 아님).
   *   팝오버가 검색 input 을 품고 있어서, 패널 전체를 다시 그리면 입력 중
   *   caret 이 날아가 한 글자만 쳐진다 — 표 검색에서 겪었던 것과 같은 함정.
   */
  private async runSearch(q: string): Promise<void> {
    const query = q.trim();
    if (query === '') {
      this.data.rowSearch = { ...this.data.rowSearch, hits: [], searching: false };
      this.rerenderSearchBody();
      return;
    }
    this.data.rowSearch = { ...this.data.rowSearch, searching: true, error: '' };
    this.rerenderSearchBody();
    const seq = ++this.rowSearchSeq;
    try {
      const res = await api.searchAll(query, 0, 20);
      if (seq !== this.rowSearchSeq) return;   // stale 응답 폐기
      this.data.rowSearch = { ...this.data.rowSearch, hits: res.catalog, searching: false };
    } catch (err) {
      if (seq !== this.rowSearchSeq) return;
      this.data.rowSearch = {
        ...this.data.rowSearch, searching: false, hits: [],
        error: err instanceof Error ? err.message : '검색 실패',
      };
    }
    this.rerenderSearchBody();
  }

  /**
   * 검색 히트 1건 → 행에 넣을 값으로 정규화.
   *
   * ★ pending(임시등록) 히트는 **카탈로그 행이 아니다**.
   *   검색 UNION 이 붙인 `source: 'pending'` 은 테이블 이름이 아니라 라벨이다.
   *   그대로 catalogSource 에 넣으면 세 가지가 동시에 깨진다 —
   *     (1) fillSpecAndShorthand 가 /api/admin/table/pending 을 쳐서 404,
   *     (2) deal_item.catalog_source 에 존재하지 않는 소스가 저장되고,
   *     (3) isUnregistered=false 라 '임시' 뱃지가 사라진다.
   *   카테고리도 source 로는 유도할 수 없다(toSpecCategory('pending') = null).
   *   등록 시 사람이 고른 값을 백엔드가 extras.category 로 내려주므로 그걸 쓴다.
   *
   * 카탈로그 히트의 동작은 종전 그대로다 — 이 함수는 분기만 담당한다.
   */
  private normalizeHit(
    source: string,
    hit:    api.CatalogSearchHit | undefined,
  ): { desc: string; spec: SpecCategory | ''; catalogSource: string | null; unregistered: boolean } {
    // pending 은 카탈로그에 없어 description_raw 가 NULL 이다 —
    // 백엔드가 extras.description → extra_description 으로 따로 내려준다.
    // 카탈로그 품목은 종전대로 description_raw 가 우선(재고 등록분은 정제 설명이 비어 있다).
    const desc = String(hit?.description_raw ?? hit?.['extra_description'] ?? '');
    if (source === ServerConfigView.PENDING_SOURCE) {
      return {
        desc,
        spec:          toSpecCategory(String(hit?.['extra_category'] ?? '')) ?? '',
        catalogSource: null,
        unregistered:  true,
      };
    }
    return {
      desc,
      spec:          toSpecCategory(source) ?? '',
      catalogSource: source || null,
      unregistered:  !source,
    };
  }

  /** 검색 결과 선택 — 명시적 선택만 카탈로그 링크를 만든다. */
  handleHitPick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-part]');
    if (!el) return;
    const targetKey = this.data.rowSearch.rowKey;
    // 팝오버가 이미 닫힌 뒤(더블클릭의 두 번째 클릭 등)에는 아무것도 하지 않는다 —
    // 대상 행이 없는데 sortRows/loadSources 만 헛돌던 경로.
    if (targetKey === null) return;
    const part = el.dataset.part ?? '';
    const source = el.dataset.source ?? '';
    // ★ 파트넘버 + 소스로 찾는다. 파트넘버만으로 찾으면 같은 파트넘버가 두 소스에
    //   있을 때(카탈로그 + 임시등록이 대표적) 클릭한 히트가 아니라 목록에서 먼저
    //   나온 히트를 집는다. 그러면 normalizeHit 이 엉뚱한 행의 설명과
    //   extra_category 를 읽어 카테고리까지 남의 것이 된다.
    const hit = this.data.rowSearch.hits.find(
      (h) => h.option_part === part && h.source === source,
    );

    const { desc, spec, catalogSource, unregistered } = this.normalizeHit(source, hit);

    // 기본 구성 모달의 행이면 그쪽에 반영한다(검색 팝오버를 공유한다).
    const baseRow = this.data.baseModal.open
      ? this.data.baseModal.items.find((r) => r.key === targetKey)
      : undefined;
    if (baseRow) {
      baseRow.optionPart        = part;
      baseRow.catalogSource     = catalogSource;
      baseRow.descriptionRaw    = desc || null;
      baseRow.description       = desc;
      baseRow.descriptionManual = false;   // 재선택 → AUTO 스냅샷 갱신
      baseRow.isUnregistered    = unregistered;
      // 품목이 바뀌면 카테고리도 새 소스에서 **무조건** 다시 유도한다(2026-08-21).
      //   예전엔 비어 있을 때만 넣었다. 그래서 한 번 박힌 카테고리는 파트를
      //   갈아끼워도 그대로 남아, memory 파트를 넣은 행이 ACCESSORY 로 남았다.
      //   ACCESSORY 는 SERVER_DESC_POLICY 가 EXCLUDED 라 그 행은 서버
      //   디스크립션에서 통째로 사라진다('RAM : Not Included').
      //
      //   ★ spec 이 '' 여도 그대로 넣는다. 빈 값은 '모름'이 아니라 **사람이
      //     지정해야 한다**는 신호다(render.ts isCategoryFixed 주석, 2026-08-20).
      //     pending 검색으로 고른 행은 extras.category 가 대부분 비어 있어
      //     '' 로 들어오고, 그때만 카테고리 select 가 열리며 '카테고리 지정 필요'
      //     경고가 뜨고 handleDone 이 완료를 막는다. 옛 값을 남기면 그 셋이
      //     전부 꺼져, 정체를 모르는 품목이 남의 카테고리를 달고 저장된다.
      baseRow.specCategory = spec;
      this.data.rowSearch = emptyRowSearch();
      this.rerenderBaseRows();
      this.rerenderSearchPanel();
      return;
    }

    let picked: ConfigRow | undefined;
    if (targetKey === '__blank__') {
      // 같은 파트(기본 아님)가 이미 있으면 새 행 대신 그 행 수량을 더한다.
      const merged = this.mergeIntoExisting(spec, part, 1);
      if (merged) {
        picked = merged;
      } else {
        // blank row → 일반 행으로 승격. 새 blank row 는 렌더가 항상 하나 유지한다.
        const row = blankRow();
        row.optionPart = part;
        row.catalogSource = catalogSource;
        row.descriptionRaw = desc || null;
        row.description = desc;
        row.specCategory = spec;
        row.isUnregistered = unregistered;
        this.data.rows.push(row);
        picked = row;
      }
    } else {
      const row = this.data.rows.find((r) => r.key === targetKey);
      if (row) {
        row.optionPart = part;
        row.catalogSource = catalogSource;
        row.descriptionRaw = desc || null;
        row.description = desc;
        row.descriptionManual = false;      // 재선택 → AUTO 스냅샷 갱신
        row.spec = null;                    // 새 품목 → 스펙 다시 조회
        row.isUnregistered = unregistered;
        row.specCategory = spec;            // 위 baseRow 와 같은 규칙(무조건 대입)
        picked = row;
      }
    }
    this.data.rowSearch = emptyRowSearch();
    this.sortRows();
    this.rerenderBody();
    // ★ 팝오버는 표(.svrcfg-grid-wrap) 밖 최상위 리전이라 rerenderBody() 로는
    //   다시 그려지지 않는다. 상태만 비우고 이 줄을 빼면 DOM 이 그대로 남아
    //   '골랐는데 드롭다운이 안 닫힌다' 가 된다(baseRow 분기는 이미 부르고 있다).
    this.rerenderSearchPanel();
    // full row 를 받아 약식으로 교체 (검색 결과엔 스펙이 부족하다)
    if (picked) void this.fillSpecAndShorthand(picked);
    void this.loadSources();   // 새 파트의 재고·RFQ 후보
  }

  // ─── Chassis 모달 ─────────────────────────────────────────────────
  handleChassisReplaceOpen(): void {
    this.closeRowSearch();
    const cur = chassisRow(this.data);
    this.data.chassisModal = {
      ...emptyChassisModal(),
      open: true, replace: true,
      pickedPart: cur?.optionPart ?? '',
      pickedDesc: cur?.description ?? '',
      pickedCondition: this.data.serverCondition || '',
    };
    this.rerender();
  }

  handleChassisSearch(e: Event): void {
    const el = e.target as HTMLInputElement;
    this.data.chassisModal = { ...this.data.chassisModal, query: el.value };
    // hits 리전만 갱신(검색 input 유지) + 디바운스
    const v = el.value;
    if (this.chassisSearchTimer) clearTimeout(this.chassisSearchTimer);
    this.chassisSearchTimer = setTimeout(
      () => { void this.runChassisSearch(v); },
      ServerConfigView.SEARCH_DEBOUNCE_MS,
    );
  }

  private async runChassisSearch(q: string): Promise<void> {
    const query = q.trim();
    // 빈/공백 검색은 카탈로그를 나열하지 않는다.
    if (query === '') {
      this.data.chassisModal = { ...this.data.chassisModal, hits: [], searching: false };
      this.rerenderChassisHits();
      return;
    }
    this.data.chassisModal = { ...this.data.chassisModal, searching: true, error: '' };
    this.rerenderChassisHits();

    const seq = ++this.chassisSearchSeq;
    try {
      // ★ 카테고리 필터를 서버에 맡긴다.
      //   검색 API 는 UNION 결과에 LIMIT 을 걸므로, 클라이언트에서 CHASSIS 만
      //   골라내면 앞 N건이 비섀시로 차서 0건이 된다("dl" → 팬·라이저 설명이 먼저 걸림).
      //   검색어에 'chassis' 토큰을 넣으면 sourceFilter 로 그 테이블만 조회한다
      //   (CATEGORY_ALIASES: chassis/섀시 → chassis, server/서버 → chassis+사전구성모델).
      const res = await api.searchAll(`chassis ${query}`, 0, 30);
      if (seq !== this.chassisSearchSeq) return;   // stale 응답 폐기
      const hits = res.catalog.filter((h) => toSpecCategory(h.source) === 'CHASSIS');
      this.data.chassisModal = { ...this.data.chassisModal, hits, searching: false };
    } catch (err) {
      if (seq !== this.chassisSearchSeq) return;
      this.data.chassisModal = {
        ...this.data.chassisModal, searching: false, hits: [],
        error: err instanceof Error ? err.message : '검색 실패',
      };
    }
    this.rerenderChassisHits();
  }

  /**
   * single click = stage 만. draft 를 바꾸지 않는다.
   *
   * ★ 여기서 결과 목록(.svrcfg-hits-region)을 다시 그리면 안 된다.
   *   클릭한 버튼 DOM 이 교체되면 뒤따르는 dblclick 이 Sola 이벤트 위임의
   *   `root.contains(matched)` 검사(SolaView.attachEvents)에서 걸러져
   *   더블클릭이 통째로 죽는다. 선택 표시는 클래스만 직접 토글하고,
   *   파생 표시물(선택됨 문구·푸터)만 리전 갱신한다.
   */
  handleChassisPick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-part]');
    if (!el) return;
    this.data.chassisModal = {
      ...this.data.chassisModal,
      pickedPart: el.dataset.part ?? '',
      pickedDesc: el.dataset.desc ?? '',
    };
    // 선택 하이라이트 — DOM 직접 토글(노드 유지)
    el.closest('.svrcfg-hits-region')
      ?.querySelectorAll('.svrcfg-hit.is-picked')
      .forEach((n) => n.classList.remove('is-picked'));
    el.classList.add('is-picked');
    // 파생 표시물만 갱신 (결과 목록은 건드리지 않는다)
    this.refreshRegion('.svrcfg-staged-region', () => renderChassisStaged(this.data));
    this.refreshRegion('.svrcfg-modal-foot-wrap', () => renderChassisFoot(this.data));
  }

  /**
   * double click — stage 후 바로 완료 경로. single click 과 같은 조건을 검증한다
   * (Chassis + 컨디션 둘 다 있어야 진행). 컨디션이 없으면 stage 만 하고 안내한다.
   */
  handleChassisPickConfirm(e: Event): void {
    this.handleChassisPick(e);
    const m = this.data.chassisModal;
    if (!m.pickedPart) return;
    if (!m.pickedCondition) {
      this.data.chassisModal = { ...m, error: '서버 컨디션을 먼저 선택해 주세요.' };
      this.rerenderChassisState();
      return;
    }
    this.handleChassisConfirm();
  }

  handleChassisCond(e: Event): void {
    const el = e.target as HTMLInputElement;
    this.data.chassisModal = {
      ...this.data.chassisModal,
      pickedCondition: el.value as 'NEW' | 'USED',
    };
    this.rerenderChassisState();   // 결과·등록·푸터만 (input 유지)
  }

  /**
   * `e` 가 선택인 이유: 액션 배선(@chassis-cancel)으로도 오지만, ESC(closeTopLayer)와
   * backdrop 핸들러에서 **인자 없이** 직접 부르기도 한다. 필수로 두면 그 두 경로가
   * `e.target` 에서 터진다(타입 에러였고 런타임 에러이기도 했다).
   */
  handleChassisCancel(e?: Event): void {
    // 최초 선택은 닫을 수 없다. replacement 만 취소 가능하며 기존 값을 모두 보존.
    if (!this.data.chassisModal.replace) return;
    if (e && (e.target as HTMLElement).classList.contains('svrcfg-modal')) return;
    this.data.chassisModal = emptyChassisModal();
    this.rerender();
  }

  // ─── 품목 등록 (카탈로그에 없는 Chassis) ──────────────────────────
  handleChassisRegisterOpen(): void {
    this.data.chassisModal = {
      ...this.data.chassisModal,
      registerOpen: true,
      // 검색어를 파트넘버 초안으로 옮겨준다(대문자 정규화).
      registerPart: this.data.chassisModal.query.trim().toUpperCase(),
    };
    this.rerenderChassisState();   // 결과·등록·푸터만 (input 유지)
  }

  handleChassisRegisterClose(): void {
    this.data.chassisModal = {
      ...this.data.chassisModal,
      registerOpen: false, registerPart: '', registerDesc: '',
    };
    this.rerenderChassisState();   // 결과·등록·푸터만 (input 유지)
  }

  handleChassisRegPart(e: Event): void {
    const el = e.target as HTMLInputElement;
    this.data.chassisModal.registerPart = el.value;
    // 이 input 이 등록 영역 안에 있어 리전을 다시 그리면 포커스가 날아간다.
    //   '등록 및 선택' 버튼 disabled 만 DOM 으로 직접 토글.
    const m = this.data.chassisModal;
    const btn = document.querySelector<HTMLButtonElement>('[data-action="chassis-reg-confirm"]');
    if (btn) btn.disabled = !(m.registerPart.trim() && m.pickedCondition);
  }

  handleChassisRegDesc(e: Event): void {
    const el = e.target as HTMLInputElement;
    this.data.chassisModal.registerDesc = el.value;
  }

  /**
   * '등록 및 선택' — 컨디션을 먼저 검사한다(누락 시 임시 품목도 만들지 않음).
   * canonical 카탈로그는 바꾸지 않는다. 저장 시 백엔드가 카탈로그 부재를 보고
   * is_unregistered 를 자동 판정해 '임시' badge 로 표시된다.
   */
  handleChassisRegisterConfirm(): void {
    const m = this.data.chassisModal;
    const part = m.registerPart.trim().toUpperCase();
    if (!m.pickedCondition) {
      this.data.chassisModal = { ...m, error: '서버 컨디션을 먼저 선택해 주세요.' };
      this.refreshRegion('.svrcfg-modal-foot-wrap', () => renderChassisFoot(this.data));
      return;
    }
    if (!part) return;
    this.data.chassisModal = {
      ...m,
      pickedPart: part,
      pickedDesc: m.registerDesc.trim(),
      registerOpen: false,
      error: '',
    };
    this.rerenderChassisState();   // 결과·등록·푸터만 (input 유지)
    // ★ 행 품목 등록과 같은 규칙으로 인테이크에 올린다 (2026-08-19).
    //   이 폼은 registerPending 을 부른 적이 없다. 카탈로그에 없는 섀시를 여기서
    //   등록하면 구성에는 담기지만 임시등록 목록에는 끝내 안 뜬다.
    //   카테고리는 CHASSIS 고정 — 이 폼이 섀시 전용이라 사람이 고를 칸이 없다.
    void this.registerPending('CHASSIS', part, m.registerDesc.trim());
  }

  // ─── 파트 후보 (장바구니) ─────────────────────────────────────────
  /**
   * Chassis 의 후보 장바구니를 불러온다.
   *
   * 원천은 chassis_candidate_part 다 — 사람이 담은 것만 온다. QuickSpec 호환
   * 목록은 추천이라 여기 오지 않고 후보 관리 모달에서만 보인다.
   *
   * 아코디언 초기 상태는 핸드오프 규정대로 — 새 구성은 전체 펼침,
   * 수정 모드는 전체 접힘. 사용자가 접고 펼친 상태는 세션 동안 유지된다.
   */
  private async loadCandidates(chassisPart: string): Promise<void> {
    if (!chassisPart) return;
    if (this.data.candidates.chassisPart === chassisPart && !this.data.candidates.error) return;

    this.data.candidates = {
      ...emptyCandidates(), chassisPart, loading: true,
    };
    // 섀시가 바뀌는 중이다. 이전 섀시의 모델명을 남겨두면 응답이 오기 전까지
    // 제목이 옛 모델로 보인다 — 비워서 약식 첫 줄 폴백으로 떨어뜨린다.
    this.data.chassisModel = null;
    this.rerenderCandidates();

    const seq = ++this.candSeq;
    try {
      const res = await api.fetchCandidates(chassisPart);
      if (seq !== this.candSeq) return;
      this.applyCandidates(chassisPart, res);
      // 구성명의 모델이 방금 확정됐다 — 제목을 다시 그린다.
      this.refreshTitles();
      // 후보 파트의 재고·RFQ 도 필요하다 — 후보 행 소스 드롭다운이 그걸 쓴다.
      void this.loadSources();
    } catch (err) {
      if (seq !== this.candSeq) return;
      this.data.candidates = {
        chassisPart, loading: false, groups: [], shareGroup: null,
        error: err instanceof Error ? err.message : '후보를 불러오지 못했습니다.',
      };
    }
    this.rerenderCandidates();
  }

  /**
   * 후보 응답을 state 에 반영한다.
   *
   * 후보 관리 저장 뒤에도 쓰이므로, 기존 그룹의 접힘/검색어/표시 상한은
   * 카테고리 기준으로 물려받는다(저장했다고 열어둔 그룹이 접히면 안 된다).
   */
  private applyCandidates(chassisPart: string, res: api.CandidatesResponse): void {
    const prev  = new Map(this.data.candidates.groups.map((g) => [g.specCategory, g]));
    const byCat = new Map(res.groups.map((g) => [g.spec_category as SpecCategory, g]));
    const openByDefault = this.data.mode === 'new';

    // ★ 담긴 후보가 없는 카테고리도 그룹을 만든다. 후보 관리 진입점이 그룹
    //   헤더의 버튼이라, 빈 카테고리를 빼면 담을 방법이 화면에서 사라진다.
    //   SPEC_CATEGORIES 가 canonical 순서라 그대로 쓰면 정렬이 필요 없다.
    const groups = SPEC_CATEGORIES
      .filter((cat) => cat !== 'CHASSIS')   // 섀시는 섀시 모달에서 고른다
      .map((cat) => {
        const g   = byCat.get(cat);
        const old = prev.get(cat);
        const count = g?.count ?? 0;
        return {
          specCategory: cat,
          count,
          items:        g?.items ?? [],
          // 새 구성은 전체 펼침, 수정 모드는 전체 접힘(핸드오프 §4).
          //   담긴 후보가 0건이어도 펼친다 — 어느 카테고리를 아직 안 채웠는지
          //   한눈에 보여야 하고, 그 자리에서 바로 후보 관리로 들어간다.
          open:         old ? old.open : openByDefault,
        };
      });

    // 구성명의 모델 자리. 후보 응답은 섀시가 정해질 때마다 오므로 이 값이
    // 곧 최신이다(수정 모드 첫 페인트 포함 — 행 스펙은 그때 아직 null 이다).
    this.data.chassisModel = res.chassis.model;

    const cg = res.candidate_group;
    this.data.candidates = {
      chassisPart, loading: false, error: '', groups,
      shareGroup: cg == null
        ? null
        : { label: cg.label, skuCount: cg.sku_count, members: cg.members },
    };
  }

  /**
   * 그룹 접기/펼치기 — 상태만 기록한다.
   *
   * native `<details>` 가 클릭의 기본 동작으로 DOM 을 이미 토글하므로 여기서
   * 다시 그리면 안 된다(그리면 두 번 토글된 것처럼 보이고, 열려 있던 다른
   * 그룹의 스크롤 위치도 튄다). 리전 재렌더 시 `open` 속성을 맞추기 위한 기록이다.
   */
  handleCandToggle(_e: Event, target: HTMLElement): void {
    const g = this.candGroup(target);
    if (g) g.open = !g.open;
  }

  private candGroup(el: HTMLElement) {
    const cat = el.dataset.cat;
    return cat
      ? this.data.candidates.groups.find((g) => g.specCategory === cat)
      : undefined;
  }

  /** 후보 행에서 고른 소스 — 선택만 기록하고 `추가` 때 구성 행에 반영한다. */
  handleCandSourceSelect(_e: Event, target: HTMLElement): void {
    const part = target.dataset.part;
    if (!part) return;
    const v = (target as HTMLSelectElement).value;
    if (v === '') delete this.data.candidateSource[part];
    else this.data.candidateSource[part] = v;
    // 다시 그리지 않는다 — select 는 이미 그 값을 보여주고 있다.
  }

  /**
   * 섀시 모델의 기본 구성을 구성 표에 주입한다(마이그 143 규정).
   *
   * 최초 Chassis 선택 / 모델이 실제로 바뀐 교체에서만 부른다. 컨디션만 바꾸거나
   * 같은 모델의 alias 로 바꾼 경우에는 기존 커스텀 부품을 보존해야 하므로
   * 호출자(handleChassisConfirm)가 `!sameModel` 을 판정한다.
   *
   * ★ 응답을 세 갈래로 나눈다(2026-08-14 현민).
   *   ⓐ exists=true + 품목 있음 → 그 목록으로 교체.
   *   ⓑ exists=false / 빈 목록  → **"이 섀시엔 기본 구성이 없다"가 확정**이다.
   *      빈 배열로 그대로 진행한다 — 이전 섀시의 기본 품목은 걷어내고 새로
   *      넣을 것만 없다. 예전엔 여기서 return 해버려 DL380 의 기본 CPU 가
   *      DL360 구성에 그대로 남았다.
   *   ⓒ 통신/서버 오류      → 있는지 없는지 **모른다**. 표를 건드리지 않고
   *      배너로 알린 뒤 저장을 막는다. 모르는 상태를 근거로 지우면 사용자는
   *      이유도 모른 채 구성을 잃고, 되돌릴 방법도 없다.
   */
  private async applyBaseParts(chassisPart: string): Promise<void> {
    const modelKey = chassisPart.trim().toUpperCase();
    // 컨디션이 아직 없으면 조회할 대상이 없다 — 섀시 확정이 컨디션을 먼저 정한다.
    const cond = this.data.serverCondition;
    if (!cond) return;
    let items: readonly api.BasePartsItem[];
    try {
      const res = await api.fetchBaseParts(modelKey, cond);
      items = res.exists ? res.items : [];            // ⓐ / ⓑ
    } catch (err) {                                    // ⓒ
      this.data.baseLoadError = {
        modelKey, condition: cond,
        message: err instanceof Error ? err.message : '기본 구성을 불러오지 못했습니다.',
      };
      this.rerenderBaseBanners();
      this.rerenderHeader();     // 완료 버튼 잠금
      return;
    }
    this.data.baseLoadError = null;
    this.rerenderBaseBanners();
    this.rerenderHeader();
    this.applyBaseItems(items);
  }

  /** 배너의 '다시 시도' — 같은 모델을 현재 컨디션으로 재조회한다. */
  handleBaseRetry(): void {
    const e = this.data.baseLoadError;
    if (!e) return;
    void this.applyBaseParts(e.modelKey);
  }

  /**
   * 기본 구성에서 담긴 행에 공통으로 얹는 표시 — 마이그 174.
   *
   * 원가를 **0(KRW)** 으로 박는다. 마이그 147 이 "미선택은 NULL, 0 으로 치환
   * 금지" 라고 못박은 것은 "원가를 모른다" 와 "원가가 진짜 0" 을 가르기
   * 위해서인데, 기본 구성 품목은 섀시 값에 포함이라 **진짜 0** 이다. 그래서
   * NULL 이 아니라 0 이고, 덕분에 매입합계·마진율이 '확인 필요' 로 막히지 않는다.
   * 소스는 비운다 — 살 물건이 아니라 딸려오는 물건이다.
   */
  private static baseRowMarks(): Pick<ConfigRow,
    'isBasePart' | 'sourceType' | 'sourceRefId' | 'sourceLabel' | 'costAmount' | 'costCurrency'> {
    return {
      isBasePart:   true,
      sourceType:   null,
      sourceRefId:  null,
      sourceLabel:  null,
      costAmount:   0,
      costCurrency: 'KRW',
    };
  }

  /**
   * 받아 둔 기본 구성 목록을 구성 표에 주입한다(조회는 하지 않는다).
   *
   * applyBaseParts(모델 변경 경로)와 컨디션 교체 배너가 함께 쓴다 — 배너는
   * 띄우기 전에 이미 items 를 받아 뒀으므로 여기서 다시 조회하면 안 된다.
   */
  private applyBaseItems(items: readonly api.BasePartsItem[]): void {
    // ① 걷어내는 건 **기본 품목뿐**이다. 사람이 직접 담은 행은 남긴다.
    //    예전엔 CHASSIS 만 남기고 전부 버려서, 섀시를 바꾸면 공들여 담은
    //    부품과 거기 넣은 판매가가 통째로 사라졌다.
    const chassisRows = this.data.rows.filter((r) => r.specCategory === 'CHASSIS');
    const userRows    = this.data.rows.filter((r) => r.specCategory !== 'CHASSIS' && !r.isBasePart);

    // ② 사라질 기본 품목의 판매단가를 파트넘버로 기억해 둔다. 새 기본 구성에
    //    같은 파트넘버가 있으면 그 값을 승계한다 — 같은 물건인데 섀시를 바꿨다고
    //    입력한 가격이 날아가면 안 된다(2026-08-14 현민).
    const priceByPart = new Map<string, number>();
    for (const r of this.data.rows) {
      if (!r.isBasePart || r.salePrice == null) continue;
      const pn = (r.optionPart ?? '').trim().toUpperCase();
      if (pn) priceByPart.set(pn, r.salePrice);
    }

    const applied = items.map((it) => {
      const pn = (it.option_part ?? '').trim().toUpperCase();
      return {
        ...blankRow(),
        specCategory:      (it.spec_category ?? '') as SpecCategory | '',
        optionPart:        it.option_part ?? '',
        // 기본 구성은 카탈로그 출처를 저장하지 않는다 — 견적 카테고리에서 유도한다
        // (약식 재계산에 필요한 full row 조회 키).
        catalogSource:     dbCategoriesFor(it.spec_category)[0] ?? null,
        description:       it.item_description ?? '',
        descriptionManual: it.description_mode !== 'AUTO',
        quantity:          it.per_server_qty,
        isUnregistered:    it.is_unregistered,
        salePrice:         pn ? (priceByPart.get(pn) ?? null) : null,
        ...ServerConfigView.baseRowMarks(),
      };
    });

    // ③ 순서는 섀시 → 기본 구성 → 직접 담은 것.
    //    sortRows() 는 카테고리로 묶고 같은 카테고리 안에서는 이 배열 순서를
    //    그대로 쓰므로, 여기서 기본을 앞에 두면 카테고리마다 기본이 위로 온다.
    this.data.rows = [...chassisRows, ...applied, ...userRows];
    this.sortRows();
    this.rerenderBody();
    for (const row of applied) void this.fillSpecAndShorthand(row);
    void this.loadSources();
  }

  // ─── 기본 구성 관리 모달 ──────────────────────────────────────────
  /**
   * ⚙ — 이 Chassis **모델**의 재사용 구성을 편집한다(Chassis 교체가 아니다).
   * 모델 키는 파트넘버 대문자다(백엔드도 대문자로 정규화).
   */
  async handleBaseOpen(): Promise<void> {
    const chassis = chassisRow(this.data)?.optionPart;
    if (!chassis) return;
    // 기본 구성은 모델×컨디션 단위다(마이그 173) — 컨디션 없이는 열지 않는다.
    const cond = this.data.serverCondition;
    if (!cond) { this.fail('서버 컨디션을 먼저 선택해 주세요.'); return; }
    this.closeRowSearch();   // 모달 안에서 다시 열 수 있다(base-search-open)
    const modelKey = chassis.trim().toUpperCase();

    this.data.baseModal = {
      ...emptyBaseModal(), open: true, loading: true, modelKey, condition: cond,
    };
    this.rerenderBaseModal();

    const seq = ++this.baseSeq;
    try {
      const res = await api.fetchBaseParts(modelKey, cond);
      if (seq !== this.baseSeq || !this.data.baseModal.open) return;
      this.data.baseModal = {
        ...this.data.baseModal,
        loading: false,
        exists:  res.exists,
        items:   res.items.map((it) => ({
          ...newBaseRow(),
          specCategory:      (it.spec_category ?? '') as SpecCategory | '',
          optionPart:        it.option_part ?? '',
          // 기본 구성은 카탈로그 출처를 저장하지 않는다(chassis_base_part_item 에
          //   컬럼이 없다) — applyBaseItems 와 똑같이 견적 카테고리에서 유도한다.
          //   빠뜨리면 null 이 되어 isCategoryFixed 가 false 가 되고, 품목이
          //   지정된 행인데 카테고리가 select 로 열린다(2026-08-12 현민 보고).
          //   ⚙ 모달 완료 경로만 그랬던 이유가 이것이다 — 모델 변경 경로는
          //   applyBaseItems 가 유도해서 채우고 있었다.
          catalogSource:     dbCategoriesFor(it.spec_category)[0] ?? null,
          description:       it.item_description ?? '',
          descriptionManual: it.description_mode !== 'AUTO',
          quantity:          it.per_server_qty,
          isUnregistered:    it.is_unregistered,
        })),
      };
    } catch (err) {
      if (seq !== this.baseSeq) return;
      this.data.baseModal = {
        ...this.data.baseModal, loading: false,
        error: err instanceof Error ? err.message : '기본 구성을 불러오지 못했습니다.',
      };
    }
    this.rerenderBaseModal();
  }

  handleBaseAdd(): void {
    this.data.baseModal.items.push(newBaseRow());
    this.rerenderBaseRows();
  }

  handleBaseDelete(_e: Event, target: HTMLElement): void {
    const key = target.dataset.rowKey;
    this.data.baseModal.items = this.data.baseModal.items.filter((r) => r.key !== key);
    this.rerenderBaseRows();
  }

  /** 품목 선택 — 구성 표와 같은 검색 팝오버를 쓴다(position:fixed 라 모달 위에 뜬다). */
  handleBaseSearchOpen(e: Event, target: HTMLElement): void {
    const key = target.dataset.rowKey;
    if (!key) return;
    this.data.rowSearch = {
      ...emptyRowSearch(), rowKey: key,
      ...ServerConfigView.anchorOf((e.target as HTMLElement).closest('td') ?? target),
    };
    this.rerenderSearchPanel();
    this.focusPanelInput();
  }

  handleBaseCategory(_e: Event, target: HTMLElement): void {
    const r = this.baseRowOf(target);
    if (r) r.specCategory = (target as HTMLSelectElement).value as SpecCategory | '';
    // 다시 그리지 않는다 — select 가 이미 그 값을 보여주고 있다.
  }

  handleBaseDesc(_e: Event, target: HTMLElement): void {
    const r = this.baseRowOf(target);
    if (!r) return;
    r.description = (target as HTMLInputElement).value;
    r.descriptionManual = true;   // 수기 수정 → MANUAL_OVERRIDE 로 저장
  }

  handleBaseQty(_e: Event, target: HTMLElement): void {
    const r = this.baseRowOf(target);
    if (!r) return;
    const n = Number((target as HTMLInputElement).value);
    r.quantity = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  }

  private baseRowOf(el: HTMLElement) {
    const key = el.dataset.rowKey;
    return key ? this.data.baseModal.items.find((r) => r.key === key) : undefined;
  }

  /**
   * 완료 — 모델에 저장하고, 현재 구성 표의 부품을 이 세트로 대체한다.
   * CHASSIS 행은 유지한다(모델 자체는 바뀌지 않았으므로).
   * 빈 목록이면 저장된 기본 구성을 지운다(유효한 완료).
   */
  async handleBaseDone(): Promise<void> {
    const m = this.data.baseModal;
    if (m.saving) return;

    const bad = m.items.find((r) => r.specCategory === '' || !r.optionPart);
    if (bad) {
      m.error = '카테고리와 품목을 모두 지정해 주세요.';
      this.rerenderBaseModal();
      return;
    }

    m.saving = true;
    m.error  = '';
    this.rerenderBaseModal();
    try {
      await api.saveBaseParts(m.modelKey, m.condition, m.items.map((r) => ({
        spec_category:    r.specCategory as string,
        option_part:      r.optionPart || null,
        is_unregistered:  r.isUnregistered,
        item_description: r.description || null,
        description_mode: r.descriptionManual ? 'MANUAL_OVERRIDE' : 'AUTO',
        per_server_qty:   r.quantity,
      })));

      // 구성 표 대체 — CHASSIS 만 남기고 기본 구성으로 채운다.
      // 여기도 기본 품목만 교체한다 — 모델의 기본 구성을 편집했다고 이 딜에
      // 직접 담아 둔 부품까지 사라지면 안 된다(섀시 변경과 같은 규칙).
      const chassis  = this.data.rows.filter((r) => r.specCategory === 'CHASSIS');
      const userKept = this.data.rows.filter((r) => r.specCategory !== 'CHASSIS' && !r.isBasePart);
      const applied = m.items.map((r) => ({
        ...blankRow(),
        specCategory:      r.specCategory,
        optionPart:        r.optionPart,
        catalogSource:     r.catalogSource,
        description:       r.description,
        descriptionRaw:    r.descriptionRaw,
        descriptionManual: r.descriptionManual,
        quantity:          r.quantity,
        isUnregistered:    r.isUnregistered,
        ...ServerConfigView.baseRowMarks(),
      }));
      this.data.rows = [...chassis, ...applied, ...userKept];
      this.sortRows();

      this.data.baseModal = emptyBaseModal();
      this.rerenderBaseModal();
      this.rerenderBody();
      // 스펙/약식은 카탈로그에서 다시 채운다(기본 구성은 스펙을 저장하지 않는다).
      for (const row of applied) void this.fillSpecAndShorthand(row);
      void this.loadSources();
    } catch (err) {
      m.saving = false;
      m.error  = err instanceof Error ? err.message : '기본 구성을 저장하지 못했습니다.';
      this.rerenderBaseModal();
    }
  }

  handleBaseCancel(): void {
    this.baseSeq += 1;   // 진행 중인 조회 무효화
    this.data.baseModal = emptyBaseModal();
    this.data.rowSearch = emptyRowSearch();
    this.rerenderBaseModal();
    this.rerenderSearchPanel();
  }

  private rerenderBaseModal(): void {
    this.refreshRegion('.svrcfg-basemodal-wrap', () => renderBaseModal(this.data));
  }
  private rerenderBaseRows(): void {
    this.refreshRegion('.svrcfg-base-region', () => renderBaseRows(this.data));
  }

  // ─── 후보 관리 모달 ───────────────────────────────────────────────
  /**
   * `[카테고리] 후보 관리` 열기.
   *
   * chip 과 그룹 헤더 버튼이 같은 핸들러를 쓴다. 그룹 헤더 버튼은 `<summary>`
   * 안에 있어 클릭이 아코디언을 토글하므로 여기서 기본 동작을 막는다.
   */
  async handleCandManageOpen(e: Event, target: HTMLElement): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    this.closeRowSearch();   // 팝오버가 모달 위에 남는 것을 막는다
    const cat = target.dataset.cat as SpecCategory | undefined;
    const chassis = chassisRow(this.data)?.optionPart;
    if (!cat || !chassis) return;

    this.data.candidateModal = {
      ...emptyCandidateModal(), open: true, specCategory: cat, loading: true,
    };
    this.rerenderCandidateModal();

    const seq = ++this.poolSeq;
    try {
      const res = await api.fetchCandidatePool(chassis, cat);
      if (seq !== this.poolSeq || !this.data.candidateModal.open) return;
      // ★ 체크는 이미 담긴 것뿐이다. 추천을 미리 체크해 두지 않는다 —
      //   사람이 담지 않은 것은 후보가 아니고, 열자마자 저장을 누르면 CPU
      //   321건이 통째로 담긴다. 추천은 배지 + 상단 정렬로만 돕는다.
      const checked = new Set(
        res.items.filter((it) => it.checked).map((it) => it.option_part),
      );
      this.data.candidateModal = {
        ...this.data.candidateModal,
        loading: false, pool: res.items, truncated: res.truncated, checked,
      };
    } catch (err) {
      if (seq !== this.poolSeq) return;
      this.data.candidateModal = {
        ...this.data.candidateModal, loading: false,
        error: err instanceof Error ? err.message : '카탈로그를 불러오지 못했습니다.',
      };
    }
    this.rerenderCandidateModal();
  }

  /** 모달 검색 — 목록 리전만 갱신해 검색 input 의 caret 을 지킨다. */
  handleCandPoolSearch(_e: Event, target: HTMLElement): void {
    const m = this.data.candidateModal;
    m.query = (target as HTMLInputElement).value;
    m.limit = CAND_LIMIT_INITIAL;   // 검색어가 바뀌면 표시 상한 초기화
    this.rerenderCandidatePool();
    // 검색은 결과 집합이 통째로 바뀐다 — 이전 스크롤을 되살리면 엉뚱한 위치에
    // 떨어진다. 표시 상한을 되돌린 것과 같은 이유로 목록도 맨 위에서 시작한다.
    const wrap = document.querySelector<HTMLElement>('.svrcfg-pool-wrap');
    if (wrap) wrap.scrollTop = 0;
  }

  handleCandPoolMore(): void {
    this.data.candidateModal.limit += CAND_LIMIT_STEP;
    this.rerenderCandidatePool();
  }

  /**
   * 개별 체크 — 파트넘버 Set 에 담는다.
   * 검색으로 행이 숨어도 체크를 잃지 않아야 해서 index 가 아니라 PN 이 키다.
   */
  handleCandCheck(_e: Event, target: HTMLElement): void {
    const part = target.dataset.part;
    if (!part) return;
    const m = this.data.candidateModal;
    if ((target as HTMLInputElement).checked) m.checked.add(part);
    else m.checked.delete(part);
    // 행 강조/선택 수/3상태만 바뀐다 — 목록 리전만 다시 그린다.
    this.rerenderCandidatePool();
    this.rerenderCandidateSelCount();
  }

  /** '전체 선택' — **보이는 결과만** 토글한다(숨은 행의 체크는 보존). */
  handleCandCheckAll(_e: Event, target: HTMLElement): void {
    const m = this.data.candidateModal;
    const visible = filterPool(m);
    const on = (target as HTMLInputElement).checked;
    for (const it of visible) {
      if (on) m.checked.add(it.option_part);
      else m.checked.delete(it.option_part);
    }
    this.rerenderCandidatePool();
    this.rerenderCandidateSelCount();
  }

  /** 저장 — 체크 집합이 곧 장바구니다(해제=삭제, 빈 집합=카테고리 비우기). */
  async handleCandSave(): Promise<void> {
    const m = this.data.candidateModal;
    const chassis = chassisRow(this.data)?.optionPart;
    if (m.saving || m.specCategory === '' || !chassis) return;

    m.saving = true;
    m.error  = '';
    this.rerenderCandidateModal();
    try {
      // 화면 순서(추천 우선 → PN)를 그대로 보낸다 → sort_order 로 저장된다.
      const ordered = m.pool
        .filter((it) => m.checked.has(it.option_part))
        .map((it) => it.option_part);
      const res = await api.saveCandidateSet(chassis, m.specCategory, ordered);
      this.applyCandidates(chassis, res);
      this.data.candidateModal = emptyCandidateModal();
      this.rerenderCandidateModal();
      this.rerenderCandidates();
      void this.loadSources();   // 새로 담은 후보 파트의 재고·RFQ
    } catch (err) {
      m.saving = false;
      m.error  = err instanceof Error ? err.message : '후보를 저장하지 못했습니다.';
      this.rerenderCandidateModal();
    }
  }

  /**
   * backdrop(모달 바깥) 클릭인가 — 안쪽 클릭이면 무시.
   *
   * Sola 위임은 closest() 라 모달 안쪽을 눌러도 조상인 backdrop 에 매칭된다.
   * 실제로 누른 요소(e.target)가 backdrop 자신일 때만 닫는다.
   */
  private static isBackdropClick(e: Event, matched: HTMLElement): boolean {
    return e.target === matched;
  }

  handleCandBackdrop(e: Event, target: HTMLElement): void {
    if (!ServerConfigView.isBackdropClick(e, target)) return;
    if (this.data.candidateModal.saving) return;
    this.handleCandCancel();
  }

  handleBaseBackdrop(e: Event, target: HTMLElement): void {
    if (!ServerConfigView.isBackdropClick(e, target)) return;
    if (this.data.baseModal.saving) return;
    this.handleBaseCancel();
  }

  handleChassisBackdrop(e: Event, target: HTMLElement): void {
    if (!ServerConfigView.isBackdropClick(e, target)) return;
    // 최초 선택(replace=false)은 취소 자체가 없다 — Chassis 없이 편집할 수 없다.
    if (!this.data.chassisModal.replace) return;
    this.handleChassisCancel();
  }

  /** 취소 — staged 편집을 버린다. 아무것도 저장되지 않는다. */
  handleCandCancel(): void {
    this.poolSeq += 1;   // 진행 중인 pool 응답 무효화
    this.data.candidateModal = emptyCandidateModal();
    this.rerenderCandidateModal();
  }

  private rerenderCandidateModal(): void {
    this.refreshRegion('.svrcfg-candmodal-wrap', () => renderCandidateModal(this.data));
    this.syncIndeterminate();
  }

  /**
   * 모달의 목록만 — 검색 input 은 리전 밖이라 포커스가 유지된다.
   *
   * 스크롤 위치를 넘겨받아 복원한다. 스크롤을 들고 있는 `.svrcfg-pool-wrap`
   * (max-height 46vh / overflow-y auto)이 이 리전 **안에** 있어서, 리전을 다시
   * 그리면 그 노드가 새로 만들어지며 scrollTop 이 0 이 된다. 591개짜리 CPU
   * 목록에서 체크 하나 누를 때마다 맨 위로 튀던 원인이 이것이다.
   *
   * scrollTop 대입은 강제로 레이아웃을 유발하므로 다시 그린 직후에 넣어도
   * 반영된다. 목록이 짧아져 범위를 넘으면 브라우저가 알아서 자른다.
   *
   * ★ 검색은 예외다 — 결과 집합 자체가 바뀌므로 호출부가 따로 맨 위로 보낸다.
   */
  private rerenderCandidatePool(): void {
    const prevTop = document.querySelector<HTMLElement>('.svrcfg-pool-wrap')?.scrollTop ?? 0;
    this.refreshRegion('.svrcfg-cand-pool-region', () => renderCandidatePool(this.data));
    if (prevTop > 0) {
      const wrap = document.querySelector<HTMLElement>('.svrcfg-pool-wrap');
      if (wrap) wrap.scrollTop = prevTop;
    }
    this.syncIndeterminate();
  }

  private rerenderCandidateSelCount(): void {
    const el = document.querySelector<HTMLElement>('.svrcfg-cand-selcount');
    if (el) el.textContent = `${this.data.candidateModal.checked.size}개 선택`;
  }

  /**
   * checkbox 의 3상태는 HTML 속성으로 표현할 수 없다(`indeterminate` 는 DOM
   * 프로퍼티뿐). 마크업에 data-indeterminate 로 남기고 렌더 후 여기서 세운다.
   */
  private syncIndeterminate(): void {
    const el = document.querySelector<HTMLInputElement>('[data-action="cand-check-all"]');
    if (el) el.indeterminate = el.dataset['indeterminate'] === '1';
  }

  /**
   * 후보 `추가` — 구성 표에 행을 넣는다(merge). 이미 있으면 아무 것도 하지 않는다.
   * 수량은 min_qty 가 있으면 그 값, 없으면 1. 소스·판매가는 구성 표에서 채운다.
   */
  handleCandidateAdd(_e: Event, target: HTMLElement): void {
    const cat  = target.dataset.cat as SpecCategory | undefined;
    const part = target.dataset.part;
    if (!cat || !part) return;

    const exists = this.data.rows.some(
      (r) => r.specCategory === cat && r.optionPart === part,
    );
    if (exists) return;

    const cand = this.data.candidates.groups
      .find((g) => g.specCategory === cat)?.items
      .find((it) => it.option_part === part);

    const row: ConfigRow = {
      ...blankRow(),
      specCategory:   cat,
      optionPart:     part,
      // 후보는 카탈로그 출처 테이블명을 함께 준다 → 약식 재계산 키.
      catalogSource:  cand?.catalog_source ?? null,
      descriptionRaw: cand?.description_raw ?? null,
      description:    cand?.description_raw ?? '',
      // 수량 기본값은 QuickSpec min_qty. 추천이 아닌 파트는 값이 없어 1 이다.
      quantity:       cand?.min_qty && cand.min_qty > 0 ? cand.min_qty : 1,
    };
    // 후보 행에서 미리 고른 소스를 함께 반영한다(선택만 해뒀던 것을 여기서 확정).
    const staged = this.data.candidateSource[part];
    if (staged) {
      this.applySourceValue(row, staged);
      delete this.data.candidateSource[part];
    }
    this.data.rows.push(row);
    this.sortRows();

    // 담았으면 그 그룹은 접는다 — 카테고리당 하나를 고르는 흐름이라,
    // 펼쳐둔 채로 두면 다음 카테고리를 찾으려 계속 스크롤해야 한다.
    const g = this.data.candidates.groups.find((x) => x.specCategory === cat);
    if (g) g.open = false;

    this.refreshRegion('.svrcfg-grid-wrap', () => renderGrid(this.data));
    this.rerenderHeader();
    // 접힘 상태는 <details open> 속성이라 body 리전만으로는 반영되지 않는다
    // → 후보 섹션을 다시 그린다. 접는 참이라 지킬 포커스도 없다.
    this.rerenderCandidates();
    void this.fillSpecAndShorthand(row);
    void this.loadSources();   // 새 파트의 재고·RFQ 후보
  }

  /** '변경 완료' 에서만 원자적으로 반영. */
  handleChassisConfirm(): void {
    const m = this.data.chassisModal;
    if (!m.pickedPart || !m.pickedCondition) return;

    const prev = chassisRow(this.data);
    const sameModel = prev?.optionPart === m.pickedPart;
    const prevCondition = this.data.serverCondition;

    this.data.serverCondition = m.pickedCondition;
    // 컨디션이 바뀌면 이전 배너는 의미가 없다(다른 컨디션 얘기였다).
    this.data.baseSwap = null;

    // 검색 결과에서 카탈로그 출처를 찾는다(chassis / pre_configured_model).
    //   품목 등록으로 만든 임시 Chassis 는 카탈로그에 없으므로 null.
    //
    // ★ 이 값은 **모델이 바뀐 경우에만** 신뢰할 수 있다. 모달은 열 때마다
    //   emptyChassisModal() 로 시작해 hits 가 비어 있고, 컨디션만 바꾸는
    //   흐름에서는 검색을 하지 않으므로 hit 이 undefined 다. 그걸 그대로
    //   반영하면 카탈로그 섀시가 임시(isUnregistered)로 강등된다.
    const hit = m.hits.find((h) => h.option_part === m.pickedPart);
    const source = hit?.source ?? null;

    let chassis: ConfigRow;
    if (prev) {
      // 같은 모델이면 구성품을 재적용·초기화하지 않는다(컨디션만 변경 케이스 포함).
      prev.optionPart = m.pickedPart;
      prev.description = m.pickedDesc;
      prev.descriptionRaw = m.pickedDesc || null;
      if (!sameModel) {
        // 카탈로그 귀속은 모델이 바뀔 때만 다시 정한다. 컨디션만 바뀌면
        // 같은 물건이므로 예전 값을 그대로 둔다(위 ★ 참조).
        prev.catalogSource = source;
        prev.isUnregistered = !source;
        prev.descriptionManual = false;
        prev.spec = null;             // 모델이 바뀌면 스펙 다시 조회
      }
      chassis = prev;
    } else {
      chassis = {
        ...blankRow(),
        key: newRowKey(),
        specCategory: 'CHASSIS',
        optionPart: m.pickedPart,
        catalogSource: source,
        descriptionRaw: m.pickedDesc || null,
        description: m.pickedDesc,
        isUnregistered: !source,
        quantity: 1,
      };
      this.data.rows.unshift(chassis);
    }
    this.data.chassisModal = emptyChassisModal();
    this.sortRows();
    this.rerender();
    // Chassis 스펙(vendor/model/sku_type/rack_unit)을 받아 약식으로 교체.
    //   다른 구성품의 stripChassis(RISER/ACCESSORY)도 이 model 을 참조한다.
    void this.fillSpecAndShorthand(chassis);
    // Chassis 가 정해졌으니 호환 후보를 불러온다(모델이 바뀌면 다시 불러옴).
    void this.loadCandidates(m.pickedPart);
    // 모델이 실제로 바뀐 경우에만 기본 구성 주입(마이그 143 규정)과 소스 재조회.
    //   소스 후보(fetchSourceCandidates)는 **파트 목록 + 딜** 로만 정해지고
    //   서버 컨디션을 인자로 받지 않는다. 컨디션만 바꾸면 같은 응답이 오는데,
    //   그동안 모든 소스 칸이 '불러오는 중…' 으로 깜빡이고 이미 고른 소스가
    //   잠깐 사라진 것처럼 보인다. 헛일이므로 하지 않는다.
    if (!sameModel) {
      void this.loadSources();   // 새 Chassis 의 재고·RFQ 후보
      void this.applyBaseParts(m.pickedPart);
    } else if (prevCondition && prevCondition !== m.pickedCondition) {
      // 같은 모델인데 컨디션만 바뀐 경우 — 기본 구성이 컨디션별로 다르다(마이그 173).
      //   자동으로 갈아끼우지 않는다. 방금 담은 부품이 말없이 사라지면 안 된다.
      void this.offerBaseSwap(m.pickedPart, m.pickedCondition);
    }
  }

  // ─── 컨디션 교체 배너 ─────────────────────────────────────────────
  /**
   * 바뀐 컨디션에 저장된 기본 구성이 **있을 때만** 배너를 띄운다.
   *
   * 없으면 아무 것도 하지 않는다 — 다른 컨디션의 구성으로 폴백하지 않는 것이
   * 마이그 173 의 규정이고, 배너가 뜬 뒤 눌렀는데 빈 목록이면 사용자는 구성이
   * 통째로 날아간 것으로 본다. 그래서 **띄우기 전에 받아 두고**, 배너가 그
   * 목록을 들고 있는다(누를 때 다시 조회하지 않는다).
   */
  private async offerBaseSwap(
    chassisPart: string,
    condition:   'NEW' | 'USED',
  ): Promise<void> {
    const modelKey = chassisPart.trim().toUpperCase();
    try {
      const res = await api.fetchBaseParts(modelKey, condition);
      if (!res.exists || res.items.length === 0) return;
      // 그 사이 사용자가 컨디션을 또 바꿨으면 낡은 응답이다.
      if (this.data.serverCondition !== condition) return;
      this.data.baseSwap = { condition, items: res.items };
    } catch {
      return;   // 조회 실패로 화면을 막지 않는다 — ⚙ 로 직접 열면 된다.
    }
    this.rerenderBaseBanners();
  }

  handleBaseSwapApply(): void {
    const swap = this.data.baseSwap;
    if (!swap) return;
    this.data.baseSwap = null;
    this.rerenderBaseBanners();
    this.applyBaseItems(swap.items);
  }

  handleBaseSwapDismiss(): void {
    if (!this.data.baseSwap) return;
    this.data.baseSwap = null;
    this.rerenderBaseBanners();
  }

  // ─── 완료(저장) ───────────────────────────────────────────────────
  async handleDone(): Promise<void> {
    const d = this.data;
    if (d.saving) return;

    // 기본 구성을 못 불러온 상태로는 저장하지 않는다 — 표에 이전 섀시의 기본
    // 품목이 남아 있을 수 있고, 그건 사용자가 의도한 구성이 아니다.
    if (d.baseLoadError) {
      this.fail('기본 구성을 불러오지 못했습니다. 배너의 다시 시도를 눌러 주세요.');
      return;
    }

    // 검증: Chassis / 컨디션 / 각 행 카테고리·수량
    if (!chassisRow(d)) { this.fail('Chassis 를 선택해 주세요.'); return; }
    if (!d.serverCondition) { this.fail('서버 컨디션을 선택해 주세요.'); return; }
    const noCat = d.rows.find((r) => r.specCategory === '');
    if (noCat) { this.fail('카테고리가 지정되지 않은 품목이 있습니다.'); return; }

    // 판매가로는 저장을 막지 않는다 (2026-08-12 서안드레).
    //
    //   구성품 판매가는 **선택 입력**이다. 하나만 넣고 나머지를 비워 둔 채
    //   저장하려는 것이 정상적인 작업 흐름인데, 예전에는 shared 의
    //   ITEM_PRICE_INCOMPLETE / ITEM_TOTAL_MISMATCH 를 그대로 차단 사유로 써서
    //   완료가 되지 않았다.
    //
    //   shared/server-price.ts 규칙 자체는 그대로 둔다 — 그건 2026-07-29 회의로
    //   확정된 **견적 규칙**이고 견적 생성이 계속 그걸로 막는다. 여기서 바뀌는
    //   것은 "Config 저장이 그 규칙을 차단 조건으로 쓰지 않는다" 뿐이다.
    //   render.ts 의 renderHeaderNotice 주석에 이미 적혀 있던 원칙과 같다 —
    //   "필수가 되는 시점은 딜 품목 확정과 견적이며, 그 화면들이 각자 막는다".
    //
    //   합계 불일치는 여전히 화면 상단에 경고로 보인다(차단은 아님).

    const input: api.ServerUpsertInput = {
      configuration_id:    d.configId,
      server_condition:    d.serverCondition,
      // server_quantity 를 **보내지 않는다** — 대수는 딜 소유라 여기서 정할 값이
      // 아니다. 보내면 딜에서 3대로 고쳐둔 값이 구성 저장 때마다 1로 돌아간다.
      // 신규 생성은 백엔드가 1로 시작한다. (`CONFIG2-018`)
      unit_price_override: parsePrice(d.bundlePrice),
      // 서버 헤더행의 제목은 **백엔드가 만든다**(2026-08-21, serverHeaderTitle).
      //   여기서 보내도 무시된다. 예전에는 화면이 만든 약식 요약 8줄을 보내
      //   그대로 저장했는데, 그건 자식 수량으로 계산되는 파생값인데 무효화
      //   규칙이 없어 낡았다. 이제 저장값은 제목 한 줄뿐이고, 요약은 조회할
      //   때 자식행으로 만든다(backend deal/server-summary.ts).
      children: d.rows.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        spec_category:       r.specCategory as string,
        catalog_source:      r.catalogSource,
        option_part:         r.optionPart || null,
        item_description:    r.description || null,
        // 위 문구의 출처를 같이 보낸다 — 마이그 191. 안 보내면 서버가 AUTO 로
        // 두므로, 사람이 타이핑한 행이 다음에 열릴 때 약식으로 덮인다.
        description_mode:    r.descriptionManual ? 'MANUAL_OVERRIDE' : 'AUTO',
        quantity:            r.quantity,
        sell_unit_price_krw: r.salePrice,
        is_unregistered:     r.isUnregistered,
        is_base_part:        r.isBasePart,
        source_type:         r.sourceType,
        source_ref_id:       r.sourceRefId,
        source_label:        r.sourceLabel,
        cost_basis_amount:   r.costAmount,
        cost_basis_currency: r.costCurrency,
      })),
    };

    d.saving = true; d.error = ''; this.rerenderHeader();
    try {
      if (d.mode === 'edit' && d.serverItemId) {
        await api.updateDealServer(d.serverItemId, input, d.expectedVersion);
      } else {
        await api.createDealServer(d.dealId, input, d.expectedVersion);
      }
      this.returnToDeal(d.dealId);
    } catch (err) {
      d.saving = false;
      d.error = err instanceof Error ? err.message : '저장 실패';
      this.rerenderHeader();
    }
  }

  private fail(msg: string): void {
    this.data.error = msg;
    this.rerenderHeader();
  }

  /**
   * 완료 후 복귀 — 이 편집기는 딜 상세에서 **새 탭**으로 열린다.
   *
   * 원래 딜 탭이 갱신되고 이 탭은 닫혀야 한다. 이 탭을 딜 화면으로 바꾸면
   * 딜 탭이 둘이 되고, 원래 탭은 낡은 화면을 계속 보여준다.
   *
   * 순서:
   *   1) BroadcastChannel 로 같은 오리진의 딜 탭들에 알린다(여러 개여도 전부).
   *   2) 이 탭을 닫는다.
   *   3) 안 닫히면(주소창에 URL 을 직접 쳐서 연 탭 등) 딜로 이동.
   *
   * ⚠ 이 탭을 여는 쪽(`handleServerEdit` / `handleServerAddNew`)은 `'noopener'`
   *   를 붙이면 안 된다. 브라우저는 **스크립트가 연 창만** 닫도록 허용하는데
   *   noopener 가 그 관계를 끊어 아래 `window.close()` 가 조용히 거부되고,
   *   매번 3) 폴백을 타 딜 탭이 둘이 된다. 실제로 그렇게 동작했다.
   */
  private returnToDeal(dealId: string): void {
    publishDealChanged({ type: 'deal-changed', dealId, reason: 'server-config-saved' });

    window.close();
    // close() 는 조용히 무시될 수 있다(직접 URL 로 연 탭 등). 닫혔는지 즉시
    // 알 방법이 없어 한 틱 뒤에도 살아 있으면 이동으로 폴백한다.
    setTimeout(() => {
      window.location.href = `/deal/${encodeURIComponent(dealId)}`;
    }, 150);
  }

  // ─── 정렬 / 부분 갱신 ─────────────────────────────────────────────
  /**
   * canonical 카테고리 순서(CHASSIS→CPU→RAM→SSD→…→NON-HW)로 정렬한다.
   *
   * 이 순서가 그대로 `deal_item.sort_order` 로 저장되고, 딜 상세의 하위 BOM·
   * 견적서 라인 순서가 된다. 즉 편집기에서 보이는 순서가 출력 순서다.
   * 같은 카테고리 안에서는 추가한 순서를 유지하고(RAM 2종 등), 카테고리를
   * 아직 못 정한 행(빈 값)은 맨 뒤로 보낸다.
   */
  /**
   * 같은 파트를 담을 때 기존 일반 행에 수량을 더한다 (2026-08-24 현민).
   *
   * 판정: **카테고리 + 파트넘버가 같고 둘 다 기본이 아닌** 위쪽 행 하나.
   *   · 파트넘버가 빈 행은 절대 합치지 않는다(임시등록 중 미입력).
   *   · 기본 구성 행(isBasePart)은 수량이 잠겨 있어 대상이 아니다 — 같은
   *     파트라도 새 일반 행을 만든다(복제 ⧉ 와 같은 결과).
   *   · 사람이 디스크립션을 직접 고친 행(descriptionManual)도 건드리지 않는다
   *     — 합치면 그 문구가 어느 쪽 것인지 모호해진다.
   *
   * 합쳤으면 그 행을 반환(호출부가 스펙·약식을 다시 만든다), 없으면 null.
   */
  private mergeIntoExisting(
    category: SpecCategory | '',
    part:     string,
    addQty:   number,
  ): ConfigRow | null {
    const pn = part.trim().toUpperCase();
    if (pn === '' || category === '') return null;
    const hit = this.data.rows.find((r) =>
      !r.isBasePart
      && !r.descriptionManual
      && r.specCategory === category
      && (r.optionPart ?? '').trim().toUpperCase() === pn);
    if (!hit) return null;
    hit.quantity = Math.max(1, Math.trunc(hit.quantity || 1)) + Math.max(1, Math.trunc(addQty || 1));
    return hit;
  }

  private sortRows(): void {
    this.data.rows = this.data.rows
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const d = specCategoryOrder(a.r.specCategory) - specCategoryOrder(b.r.specCategory);
        return d !== 0 ? d : a.i - b.i;
      })
      .map((x) => x.r);
  }

  // ─── 부분 갱신 (coreSolaView.refreshRegion) ────────────────────────
  //
  // ★ 입력(input) 이벤트에서는 그 input 이 든 리전을 절대 다시 그리지 않는다.
  //   다시 그리면 DOM 이 교체되어 포커스·caret 이 날아가 한 글자만 입력된다.
  //   그래서 "값이 바뀌면 따라 변하는 표시물"만 별도 리전으로 쪼개 갱신한다.

  /** 금액/수량 입력 중 — 경고문구 + 합계만. input 은 건드리지 않는다. */
  private rerenderPriceNotice(): void {
    this.refreshRegion('.svrcfg-warn-region', () => renderHeaderNotice(this.data));
    this.refreshRegion('.svrcfg-sum-region', () => renderTotals(this.data));
  }

  /** 품목 검색 결과만 (표 밖 상시 리전) — 검색 input 유지. */
  /**
   * 검색 결과만 — 팝오버 안의 검색 input 은 그대로 둔다.
   *
   * ★ 팝오버가 아직 DOM 에 없으면(빈 행에서 타이핑을 시작한 경우) 갱신할 리전이
   *   없어 아무것도 뜨지 않는다. 그때는 팝오버 전체를 그린다.
   *   ('새 품목 추가' 검색은 표 안 input 으로 시작하므로 이 경로를 반드시 탄다)
   */
  private rerenderSearchBody(): void {
    if (!document.querySelector('.svrcfg-panel-body')) {
      this.rerenderSearchPanel();
      return;
    }
    this.refreshRegion('.svrcfg-panel-body', () => renderRowSearchBody(this.data));
  }

  private rerenderSearchPanel(): void {
    this.refreshRegion('.svrcfg-searchpanel-wrap', () => renderRowSearchPanel(this.data));
  }

  /** Chassis 검색 결과만 — 모달의 검색 input 유지. */
  private rerenderChassisHits(): void {
    this.refreshRegion('.svrcfg-hits-region', () => renderChassisHits(this.data));
    this.refreshRegion('.svrcfg-staged-region', () => renderChassisStaged(this.data));
  }

  /** 선택 상태가 바뀔 때 — 결과·등록영역·푸터(disabled)만. */
  private rerenderChassisState(): void {
    // 결과 목록은 제외 — 더블클릭 대상 노드를 살려둔다(위 handleChassisPick 주석).
    this.refreshRegion('.svrcfg-staged-region', () => renderChassisStaged(this.data));
    this.refreshRegion('.svrcfg-register-region', () => renderChassisRegisterRegion(this.data));
    this.refreshRegion('.svrcfg-modal-foot-wrap', () => renderChassisFoot(this.data));
  }

  /**
   * 한 행의 디스크립션 input 값만 DOM 으로 갱신.
   * 수량 입력 중 표를 다시 그리면 그 input 의 포커스가 날아가므로 값만 넣는다.
   */
  private rerenderDescCell(row: ConfigRow): void {
    const el = document.querySelector<HTMLInputElement>(
      `[data-action="desc-input"][data-row-key="${row.key}"]`,
    );
    // ★ 렌더와 **같은 함수**를 쓴다. 예전엔 여기만 row.description(약식)을
    //   써넣어서, 처음엔 원문이 보이다가 수량을 건드리는 순간 약식으로
    //   바뀌었다(2026-08-14). 표시값의 정본은 descCellValue 하나다.
    const shown = descCellValue(row);
    if (el && el.value !== shown) el.value = shown;
  }

  /** 헤더 전체(모델·컨디션·완료 버튼 상태) — 입력 중에는 쓰지 않는다. */
  private rerenderHeader(): void {
    this.refreshRegion('.svrcfg-header-wrap', () => renderHeader(this.data));
  }
  private rerenderBody(): void {
    this.refreshRegion('.svrcfg-grid-wrap', () => renderGrid(this.data));
    this.rerenderCandidates();   // '추가' ↔ '추가됨' 동기화
    this.rerenderHeader();       // 합계·경고 동기화
  }
  private rerenderCandidates(): void {
    this.refreshRegion('.svrcfg-cand-wrap', () => renderCandidates(this.data));
  }
  /**
   * 구성명(H1 + 브라우저 탭) 재동기화.
   *
   * 수정 모드는 진입 시점에 섀시 모델을 모른다 — 행 스펙은 lazy 조회라 null 이고
   * `chassis.model` 은 후보 응답에 실려 온다. 그래서 첫 페인트는 약식 첫 줄
   * 폴백(`HPE DL380p Gen8 8SFF (2U)`)으로 나가고, 응답이 오면 정식 모델
   * (`DL380p Gen8`)로 올라간다.
   *
   * 탭 제목까지 같이 손대는 이유: 라우터는 진입할 때 `getTitle()` 을 한 번만
   * 읽는다(SolaRouter.ts:489). 그대로 두면 H1 은 정식 모델인데 탭만 폴백으로
   * 남아 두 이름이 갈라진다.
   */
  private refreshTitles(): void {
    this.rerenderHeader();
    document.title = this.getTitle();
  }
  /** 기본 구성 관련 배너(조회 실패 + 컨디션 교체 제안) — 같은 자리에 쌓인다. */
  private rerenderBaseBanners(): void {
    this.refreshRegion('.svrcfg-swap-wrap', () => renderBaseBanners(this.data));
  }
  /** 모달만 — 검색/등록 입력 중 grid 를 다시 그리지 않도록 분리. */
  private rerenderModal(): void {
    this.refreshRegion('.svrcfg-modal-wrap', () => renderChassisModal(this.data));
  }
  private rerender(): void {
    this.rerenderModal();
    this.rerenderBody();
  }
}
